import type { CSSProperties, MouseEvent as ReactMouseEvent, PointerEvent as ReactPointerEvent } from 'react';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Checkbox,
  DatePicker,
  Dropdown,
  Modal,
  Input,
  Popover,
  Select,
  Space,
  Splitter,
  Tabs,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import { CopyOutlined, DeleteOutlined, DoubleLeftOutlined, DoubleRightOutlined, DownOutlined, EditOutlined, ExpandAltOutlined, HolderOutlined, LinkOutlined, LockOutlined, MoreOutlined, ReloadOutlined, SettingOutlined, ShrinkOutlined, SwapOutlined, ThunderboltOutlined, UndoOutlined, UpOutlined } from '@ant-design/icons';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import type { DragEndEvent } from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  horizontalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useDrawerSettings, COMPACT_WIDTH } from '../../shared/hooks/useDrawerSettings.ts';
import dayjs from 'dayjs';
import { dateFieldValue, datePickerFormat } from '../../shared/lib/dateValue';
import type { FieldDefinition, ItemRow, ItemType, LinkRule, SpaceMember, SpaceUserGroup } from '@lens/shared';
import { resolveTitleField } from '@lens/shared';
import { applyLinkRuleBulk, listLinkRules } from '../link-rules/api.ts';
import { changeItemType } from './api.ts';
import { listSpaceUserGroups } from '../spaces/api.ts';
import {
  expandUserGroupSelection,
  withUserGroupOptions,
} from '../spaces/userGroupOptions.ts';
import { renderUserBulkPopup } from '../spaces/UserBulkActions.tsx';
import { CommentSection } from '../comments/CommentSection.tsx';
import { listItemComments } from '../comments/api.ts';
import { AuditHistory } from '../audit/AuditHistory.tsx';
import { getItemHistory } from '../audit/api.ts';
import { AiSuggestButton } from '../ai/AiSuggestButton.tsx';
import { ItemAiChat } from '../ai/ItemAiChat.tsx';
import { LinkedItemsTable } from './LinkedItemsTable.tsx';
import { ItemWhiteboard } from '../whiteboard/ItemWhiteboard.tsx';
import { renderFieldValue, fieldValueToText } from '../../shared/fields/renderFieldValue.tsx';
import { MarkdownEditorModal } from '../../shared/fields/MarkdownEditorModal.tsx';
import { MarkdownInlineEditor } from '../../shared/fields/MarkdownInlineEditor.tsx';
import { apiRequest, ApiError } from '../../shared/api/client.ts';
import { uploadAttachment } from '../../shared/api/attachments.ts';
import { getDictionary, listEntries } from '../dictionaries/api.ts';
import { entryLabel } from '../dictionaries/labelHelpers.ts';
import { refreshItemJiraStatus, fetchJiraSnapshot, updateSyncConfig, linkItemToJira, unlinkItemFromJira } from '../jira/api.ts';
import type { JiraSnapshotField } from '../jira/api.ts';
import { JiraIssueLink } from '../../shared/components/JiraIssueLink.tsx';
import { JiraCompareModal } from '../jira/JiraCompareModal.tsx';
import { EstimateRecordDrawer } from '../hermes/EstimateRecordDrawer.tsx';
import { ItemReactionBar } from '../reactions/ItemReactionBar.tsx';
import type { StatusCategoryKey } from '../../shared/theme/tokens.ts';
import styles from './ItemDrawer.module.css';

import type { AiSuggestKind } from '@lens/shared';

const { Text } = Typography;

/**
 * Определяет подходящий AI-suggest по типу/slug поля.
 * Возвращает null, если для этого поля подсказка не предусмотрена.
 */
function getSuggestKind(field: FieldDefinition): AiSuggestKind | null {
  const slug = field.slug.toLowerCase();
  if (slug.includes('criteria') || slug.includes('dod') || slug.includes('критери')) {
    return 'clarify_criteria';
  }
  if (field.fieldType === 'select' && (slug.includes('categ') || slug.includes('категори'))) {
    return 'suggest_category';
  }
  if (field.fieldType === 'text' || field.fieldType === 'textarea' || field.fieldType === 'markdown') {
    return 'clarify_title';
  }
  return null;
}

function getSuggestTooltip(kind: AiSuggestKind): string {
  switch (kind) {
    case 'clarify_title': return 'Lensy: уточнить формулировку текста';
    case 'clarify_criteria': return 'Lensy: улучшить критерии';
    case 'suggest_category': return 'Lensy: предложить категорию';
    case 'summarize_comments': return 'Lensy: саммари комментариев';
  }
}

/**
 * Если поле — статусное (select c config.hasCategories), вернуть JSX-чип
 * категории по statusCategory: подложка bg, текст/точка color, лейбл из
 * option.label (НЕ сырой value). Иначе — null (поле рендерится обычным путём).
 */
function renderStatusChip(field: FieldDefinition, value: unknown): JSX.Element | null {
  if (field.fieldType !== 'select') return null;
  const config = field.config as
    | { hasCategories?: boolean; options?: Array<{ value: string; label?: string; category?: string }> }
    | null;
  if (!config?.hasCategories) return null;
  if (typeof value !== 'string' || value === '') return null;
  const option = (config.options ?? []).find((o) => o.value === value);
  if (!option) return null;
  const cat = (option.category ?? 'backlog') as StatusCategoryKey;
  // Тема-зависимые токены статус-чипа (var(--status-*)): светлая мягкая
  // подложка в светлой теме, тёмный тинт — в тёмной (не белый блок).
  return (
    <span
      className={styles.statusChip}
      style={{ background: `var(--status-${cat}-bg)`, color: `var(--status-${cat}-fg)` }}
    >
      <span className={styles.statusDot} style={{ background: `var(--status-${cat}-dot)` }} />
      <span className={styles.statusChipText}>{option.label || option.value}</span>
    </span>
  );
}

interface ItemDrawerProps {
  open: boolean;
  onClose: () => void;
  item: ItemRow | null;
  fields: FieldDefinition[];
  spaceId: string;
  spaceSlug?: string;
  itemTypeId?: string;
  currentUserId: string;
  myRole?: string;
  jiraEnabled?: boolean;
  hermesEnabled?: boolean;
  resolvedRefs?: Record<string, string>;
  onFieldSave?: (fieldSlug: string, value: unknown) => void;
  onDelete?: (itemId: string) => void;
  onDuplicate?: (itemId: string) => void;
  onLinkedItemClick?: (itemId: string, targetSpaceId?: string) => void;
  /** Все типы записей пространства — для пункта «Сменить тип». */
  availableTypes?: ItemType[];
  /** Вызывается после успешной смены типа записи. */
  onTypeChanged?: (itemId: string) => void;
  /**
   * Листание записей текущего представления не закрывая карточку:
   * -1 — предыдущая, 1 — следующая (в порядке видимых строк грида).
   */
  onNavigate?: (direction: 1 | -1) => void;
  /** Позиция открытой записи в представлении (для счётчика «3 / 22»). */
  navPosition?: { index: number; total: number } | null;
}

// ─── Reference field selector ───────────────────────────────────────

interface RefSelectorProps {
  value: unknown;
  field: FieldDefinition;
  resolvedRefs?: Record<string, string>;
  onSave: (value: unknown) => void;
}

function RefSelector({ value, field, resolvedRefs, onSave }: RefSelectorProps): JSX.Element {
  const [editing, setEditing] = useState(false);
  const [options, setOptions] = useState<Array<{ value: string; label: string }>>([]);
  const [loadingOpts, setLoadingOpts] = useState(false);
  const config = field.config as { dictionaryId?: string; multiple?: boolean } | null;
  const isMulti = config?.multiple ?? false;

  const loadOptions = useCallback(async () => {
    if (!config?.dictionaryId) return;
    setLoadingOpts(true);
    try {
      const [dict, res] = await Promise.all([
        getDictionary(config.dictionaryId),
        listEntries(config.dictionaryId, { limit: 200 }),
      ]);
      const opts = res.items.map((e) => ({ value: e.id, label: entryLabel(e, dict) }));
      setOptions(opts);
    } catch {
      // ignore
    } finally {
      setLoadingOpts(false);
    }
  }, [config?.dictionaryId]);

  const renderReferenceValue = () => {
    const labelById = new Map(options.map((option) => [option.value, option.label]));
    if (isMulti && Array.isArray(value)) {
      const labels = value.map((entryId) => labelById.get(String(entryId)) ?? resolvedRefs?.[String(entryId)] ?? String(entryId));
      return labels.length > 0 ? labels.join(', ') : '—';
    }
    if (typeof value === 'string') {
      return labelById.get(value) ?? resolvedRefs?.[value] ?? value;
    }
    return null;
  };

  if (!editing) {
    return (
      <div
        onClick={() => { setEditing(true); void loadOptions(); }}
        className={getEditableFieldClassName('pointer')}
      >
        {renderReferenceValue() ?? renderFieldValue(value, field, resolvedRefs)}
      </div>
    );
  }

  return (
    <Select
      size="small"
      mode={isMulti ? 'multiple' : undefined}
      value={isMulti ? (Array.isArray(value) ? value : []) : (typeof value === 'string' ? value : undefined)}
      style={{ width: '100%' }}
      // Колонка поля узкая — попап шире неё, иначе имена обрезаются многоточием.
      popupMatchSelectWidth={false}
      styles={{ popup: { root: { minWidth: 240, maxWidth: 420 } } }}
      allowClear
      loading={loadingOpts}
      options={options}
      showSearch
      filterOption={(input, opt) => String(opt?.label ?? '').toLowerCase().includes(input.toLowerCase())}
      onChange={(v) => { onSave(v ?? null); setEditing(false); }}
      onBlur={() => setEditing(false)}
      autoFocus
      open
      placeholder="Выберите..."
    />
  );
}

interface UserSelectorProps {
  value: unknown;
  field: FieldDefinition;
  resolvedRefs?: Record<string, string>;
  onSave: (value: unknown) => void;
  spaceId: string;
}

function UserSelector({ value, field, resolvedRefs, onSave, spaceId }: UserSelectorProps): JSX.Element {
  const [editing, setEditing] = useState(false);
  const [options, setOptions] = useState<Array<{ value: string; label: string }>>([]);
  // Группы пространства — отдельный раздел списка: выбор группы подставляет
  // её участников по отдельности (см. features/spaces/userGroupOptions.ts).
  const [userGroups, setUserGroups] = useState<SpaceUserGroup[]>([]);
  const [loadingOpts, setLoadingOpts] = useState(false);
  const config = field.config as { multiple?: boolean } | null;
  const isMulti = config?.multiple ?? false;

  const loadOptions = useCallback(async () => {
    setLoadingOpts(true);
    try {
      const res = await apiRequest<{ members: SpaceMember[] }>(`/api/spaces/${spaceId}/members?limit=200`);
      setOptions(res.members.map((m) => ({ value: m.userId, label: m.displayName || m.login })));
      if (isMulti) {
        const groupsRes = await listSpaceUserGroups(spaceId).catch(() => ({ groups: [] }));
        setUserGroups(groupsRes.groups);
      }
    } catch {
      // ignore
    } finally {
      setLoadingOpts(false);
    }
  }, [isMulti, spaceId]);

  const renderUserValue = () => {
    const labelById = new Map(options.map((option) => [option.value, option.label]));
    if (isMulti && Array.isArray(value)) {
      const labels = value.map((userId) => labelById.get(String(userId)) ?? resolvedRefs?.[String(userId)] ?? String(userId));
      return labels.length > 0 ? labels.join(', ') : '—';
    }
    if (typeof value === 'string') {
      return labelById.get(value) ?? resolvedRefs?.[value] ?? value;
    }
    return null;
  };

  if (!editing) {
    return (
      <div
        onClick={() => { setEditing(true); void loadOptions(); }}
        className={getEditableFieldClassName('pointer')}
      >
        {renderUserValue() ?? renderFieldValue(value, field, resolvedRefs)}
      </div>
    );
  }

  return (
    <Select
      size="small"
      mode={isMulti ? 'multiple' : undefined}
      value={isMulti ? (Array.isArray(value) ? value : []) : (typeof value === 'string' ? value : undefined)}
      style={{ width: '100%' }}
      // Колонка поля узкая — попап шире неё, иначе имена обрезаются многоточием.
      popupMatchSelectWidth={false}
      styles={{ popup: { root: { minWidth: 240, maxWidth: 420 } } }}
      allowClear
      loading={loadingOpts}
      options={withUserGroupOptions(options, userGroups, isMulti)}
      showSearch
      filterOption={(input, opt) => String(opt?.label ?? '').toLowerCase().includes(input.toLowerCase())}
      popupRender={
        isMulti
          ? (menu) =>
              renderUserBulkPopup(menu, options.map((o) => o.value), value, (next) => {
                onSave(next.length > 0 ? next : null);
                setEditing(false);
              })
          : undefined
      }
      onChange={(v) => {
        // Выбранная группа тут же превращается в её участников — в поле
        // видно пользователей, а не название группы.
        onSave(expandUserGroupSelection(v, userGroups) ?? null);
        setEditing(false);
      }}
      onBlur={() => setEditing(false)}
      autoFocus
      open
      placeholder="Выберите..."
    />
  );
}

// ─── Editable field cell for drawer ───────────────────────────────────────

export interface DrawerFieldProps {
  value: unknown;
  field: FieldDefinition;
  resolvedRefs?: Record<string, string>;
  spaceId: string;
  onSave: (value: unknown) => void;
  /**
   * Открыть полноразмерный редактор markdown/textarea внутри карточки
   * (вместо вложенной модалки). Если не передан — кнопка expand скрыта.
   */
  onExpand?: (value: string) => void;
  /** Загрузчик изображений для markdown-редактора (cmd+v / drag-drop). */
  onUploadImage?: (file: File) => Promise<string>;
  /**
   * Готовый JSX-чип статус-категории для select-полей с hasCategories.
   * Отрисовывается вместо renderFieldValue в режиме просмотра, click-to-edit
   * сохраняется. Для остальных полей не передаётся.
   */
  statusChip?: JSX.Element | null;
  /**
   * Единица измерения number-поля (напр. «%»). Рендерится ВНУТРИ кликабельного
   * значения, вплотную к числу — а не отдельным сиблингом, который из-за
   * width:100% click-target'а уезжал к правому краю колонки.
   */
  unit?: string | null;
}

/**
 * Span поля в гриде «Свойств» — по ФАКТИЧЕСКОМУ значению, а не по типу поля.
 * Состав полей гибкий (задаёт админ), поэтому типовая классификация
 * «короткий/длинный» промахивается: reference с одним коротким чипом занимал
 * всю строку, а select с длинной опцией жался в пол-ячейки. Считаем
 * человекочитаемый текст значения (fieldValueToText — то же, что видит юзер)
 * и решаем: короткое → одна ячейка грида, длинное или мультизначение →
 * вся строка. Пустые значения — всегда ячейка (это просто «—»).
 */
const CELL_TEXT_LIMIT = 24;
function fieldGridSpan(
  field: FieldDefinition,
  value: unknown,
  resolvedRefs?: Record<string, string>,
): 'cell' | 'row' {
  if (isEmptyFieldValue(value)) return 'cell';
  // URL — непредсказуемой длины и неразрывный токен: в узкой ячейке режется.
  // Всегда на всю строку, чтобы ссылка помещалась целиком.
  if (field.fieldType === 'url') return 'row';
  if (Array.isArray(value) && value.length > 1) return 'row';
  const text = fieldValueToText(value, field, resolvedRefs);
  return text.length <= CELL_TEXT_LIMIT ? 'cell' : 'row';
}

/** Минимум высоты нижней панели (таб-бар + разумная зона контента), px. */
const BOTTOM_PANE_MIN = 320;
/** Футер метаданных + сплиттер + вертикальные паддинги тела модалки, px. */
const DRAWER_CHROME = 48;
/** Минимальная высота тела модалки в оконном режиме, px. */
const MIN_BODY_HEIGHT = 560;

/** Ширина колонки обсуждения в карточке — ключ localStorage и границы, %. */
const TALK_WIDTH_KEY = 'lens:card:talkWidth';
function clampTalkPct(n: number): number {
  return Math.min(65, Math.max(22, n));
}

/** Пустое значение поля: null/undefined, пустая строка, пустой массив. */
function isEmptyFieldValue(value: unknown): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.trim() === '';
  if (Array.isArray(value)) return value.length === 0;
  return false;
}

/**
 * Зона карточки для поля — единственный источник правды о раскладке,
 * выведенный ТОЛЬКО из типа поля (никаких настроек и хардкода по slug):
 *   identity — статусное поле (select + hasCategories, ≤1 на тип): пилюля-
 *              состояние в шапке;
 *   body     — markdown/textarea: полноценный редактор в левой колонке;
 *   props    — всё остальное: компактная панель «Свойства».
 * Гибкая система: что бы админ ни накликал, раскладка выводится из типа,
 * а не из ручной конфигурации секций/таблиц.
 */
export type FieldZone = 'identity' | 'body' | 'props';
export function fieldZone(field: FieldDefinition): FieldZone {
  const cfg = field.config as { hasCategories?: boolean } | null;
  if (field.fieldType === 'select' && cfg?.hasCategories === true) return 'identity';
  if (field.fieldType === 'markdown' || field.fieldType === 'textarea') return 'body';
  return 'props';
}

function isFieldEditable(field: FieldDefinition): boolean {
  return field.fieldType !== 'computed'
    && !((field as FieldDefinition & { readOnly?: boolean }).readOnly === true);
}

function getEditableFieldClassName(cursor: 'text' | 'pointer'): string {
  return [
    styles.fieldValue,
    styles.fieldValueEditable,
    cursor === 'text' ? styles.fieldValueText : styles.fieldValuePointer,
  ].join(' ');
}

// Заголовок карточки в шапке: бесшовное inline-редактирование значения
// поля-заголовка. Раньше шапка была статичным <Text> — а поле-заголовок
// дедупится из «Свойств» и тела, поэтому у text/textarea-заголовка не
// оставалось ни одного места для правки. Клик по заголовку → Input,
// сохранение по Enter/blur, отмена по Escape.
function EditableHeaderTitle({
  value,
  placeholder,
  editable,
  onSave,
}: {
  value: string;
  placeholder: string;
  editable: boolean;
  onSave: (next: string) => void;
}): JSX.Element {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  useEffect(() => {
    if (!editing) setDraft(value);
  }, [value, editing]);

  // Длинный заголовок переносим на несколько строк, а не обрезаем ellipsis'ом.
  const baseStyle: React.CSSProperties = {
    whiteSpace: 'normal',
    overflowWrap: 'anywhere',
    minWidth: 0,
  };

  if (!editable) {
    return <Text strong style={baseStyle}>{value || placeholder}</Text>;
  }

  const commit = (): void => {
    setEditing(false);
    const next = draft.trim();
    if (next && next !== value) onSave(next);
    else setDraft(value);
  };

  if (editing) {
    // Авторастущий TextArea: длинный заголовок виден целиком и переносится,
    // а не прячется за краем однострочного инпута. Enter сохраняет (перенос
    // строки в заголовке не нужен), Escape отменяет.
    return (
      <Input.TextArea
        size="small"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); commit(); }
          // stopPropagation: Escape гасит только правку заголовка. Без него
          // событие долетает до Modal карточки (keyboard=true по умолчанию),
          // и вместе с правкой закрывается вся карточка.
          if (e.key === 'Escape') { e.stopPropagation(); setDraft(value); setEditing(false); }
        }}
        autoFocus
        autoSize
        placeholder={placeholder}
        style={{ fontWeight: 600, padding: '0 4px', flex: 1, minWidth: 0 }}
      />
    );
  }

  return (
    <Tooltip title="Редактировать заголовок">
      <Text
        strong
        onClick={() => setEditing(true)}
        style={{ ...baseStyle, cursor: 'text' }}
      >
        {value || placeholder}
      </Text>
    </Tooltip>
  );
}

function ReadOnlyFieldValue({
  value,
  field,
  resolvedRefs,
}: {
  value: unknown;
  field: FieldDefinition;
  resolvedRefs?: Record<string, string>;
}): JSX.Element {
  const tooltipTitle = field.fieldType === 'computed' ? 'Вычисляемое поле' : 'Только чтение';
  return (
    <Tooltip title={tooltipTitle}>
      <div className={styles.fieldValueReadonly}>
        <span className={styles.fieldValueLock} aria-label={tooltipTitle}>
          <LockOutlined />
        </span>
        <span className={styles.fieldValueContent}>
          {renderFieldValue(value, field, resolvedRefs)}
        </span>
      </div>
    </Tooltip>
  );
}

export function DrawerField({ value, field, resolvedRefs, spaceId, onSave, onExpand, onUploadImage, statusChip, unit }: DrawerFieldProps): JSX.Element {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');

  if (!isFieldEditable(field)) {
    return <ReadOnlyFieldValue value={value} field={field} resolvedRefs={resolvedRefs} />;
  }

  // Reference fields use RefSelector
  if (field.fieldType === 'reference') {
    return <RefSelector value={value} field={field} resolvedRefs={resolvedRefs} onSave={onSave} />;
  }
  if (field.fieldType === 'user') {
    return <UserSelector value={value} field={field} resolvedRefs={resolvedRefs} onSave={onSave} spaceId={spaceId} />;
  }

  const startEdit = () => {
    setDraft(value !== null && value !== undefined ? String(value) : '');
    setEditing(true);
  };

  const commit = () => {
    setEditing(false);
    let parsed: unknown = draft || null;
    if (field.fieldType === 'number') {
      const n = Number(draft);
      parsed = draft.trim() === '' ? null : Number.isNaN(n) ? null : n;
    }
    if (parsed !== value) onSave(parsed);
  };

  // Select field
  if (field.fieldType === 'select') {
    const config = field.config as { options?: Array<{ value: string; label: string }> };
    const options = config.options ?? [];
    if (!editing) {
      return (
        <div onClick={startEdit} className={getEditableFieldClassName('pointer')}>
          {statusChip ?? renderFieldValue(value, field, resolvedRefs)}
        </div>
      );
    }
    return (
      <Select
        size="small"
        value={typeof value === 'string' ? value : undefined}
        style={{ width: '100%' }}
        popupMatchSelectWidth={false}
        allowClear
        options={options.map((o) => ({ value: o.value, label: o.label || o.value }))}
        onChange={(v) => { onSave(v ?? null); setEditing(false); }}
        autoFocus
        open
        onBlur={() => setEditing(false)}
      />
    );
  }

  // Multi-select field
  if (field.fieldType === 'multi_select') {
    const config = field.config as { options?: Array<{ value: string; label: string }> };
    const options = config.options ?? [];
    if (!editing) {
      return (
        <div onClick={startEdit} className={getEditableFieldClassName('pointer')}>
          {renderFieldValue(value, field, resolvedRefs)}
        </div>
      );
    }
    return (
      <Select
        size="small"
        mode="multiple"
        value={Array.isArray(value) ? value : []}
        style={{ width: '100%' }}
        popupMatchSelectWidth={false}
        options={options.map((o) => ({ value: o.value, label: o.label || o.value }))}
        onChange={(v) => { onSave(v.length > 0 ? v : null); setEditing(false); }}
        autoFocus
        open
        onBlur={() => setEditing(false)}
      />
    );
  }

  // Boolean field
  if (field.fieldType === 'boolean') {
    if (!editing) {
      return (
        <div onClick={startEdit} className={getEditableFieldClassName('pointer')}>
          {renderFieldValue(value, field, resolvedRefs)}
        </div>
      );
    }
    return (
      <Select
        size="small"
        value={typeof value === 'boolean' ? value : undefined}
        style={{ width: '100%' }}
        allowClear
        options={[
          { value: true, label: 'Да' },
          { value: false, label: 'Нет' },
        ]}
        onChange={(v) => { onSave(v ?? null); setEditing(false); }}
        autoFocus
        open
        onBlur={() => setEditing(false)}
      />
    );
  }

  // Date field — AntD DatePicker
  if (field.fieldType === 'date') {
    const config = field.config as { includeTime?: boolean };
    if (!editing) {
      return (
        <div onClick={startEdit} className={getEditableFieldClassName('pointer')}>
          {renderFieldValue(value, field, resolvedRefs)}
        </div>
      );
    }
    return (
      <DatePicker
        size="small"
        showTime={config.includeTime ?? false}
        format={datePickerFormat(config.includeTime ?? false)}
        value={value ? dayjs(String(value)) : null}
        onChange={(d) => {
          onSave(dateFieldValue(d, config.includeTime ?? false));
          setEditing(false);
        }}
        // Не onBlur: клик по стрелкам месяца/года в панели календаря уводит
        // фокус с инпута, и blur закрывал бы редактор до переключения месяца
        onOpenChange={(open) => {
          if (!open) setEditing(false);
        }}
        autoFocus
        open
        style={{ width: '100%' }}
      />
    );
  }

  // Markdown / textarea — в режиме чтения карточка выглядит как документ:
  // готовый контент без рамки редактора, resize-handle и постоянных иконок.
  // Редактор (Tiptap для markdown, TextArea для textarea) монтируется только
  // по клику (edit-on-click) или по иконке-карандашу на hover. Пустое поле —
  // компактный приглушённый плейсхолдер в одну строку.
  if (field.fieldType === 'markdown' || field.fieldType === 'textarea') {
    const isMarkdown = field.fieldType === 'markdown';
    const hasValue = value !== null && value !== undefined && String(value).trim() !== '';

    // Режим чтения — спокойный документ.
    if (!editing) {
      const beginEdit = () => {
        setDraft(value !== null && value !== undefined ? String(value) : '');
        setEditing(true);
      };
      // Клик по ссылке в тексте — это переход, а не начало правки.
      const beginEditFromClick = (e: ReactMouseEvent) => {
        if ((e.target as HTMLElement).closest('a[href]')) return;
        beginEdit();
      };
      return (
        <div className={styles.docRead} onClick={beginEditFromClick}>
          {hasValue ? (
            <div className={styles.docReadValue}>
              {renderFieldValue(value, field, resolvedRefs)}
            </div>
          ) : (
            <span className={styles.docPlaceholder}>Добавьте описание…</span>
          )}
          <span className={styles.docActions} onClick={(e) => e.stopPropagation()}>
            <Tooltip title="Редактировать">
              <Button
                size="small"
                type="text"
                icon={<EditOutlined />}
                onClick={beginEdit}
              />
            </Tooltip>
            {onExpand && (
              <Tooltip title="Открыть в большом окне">
                <Button
                  size="small"
                  type="text"
                  icon={<ExpandAltOutlined />}
                  onClick={() => { onExpand(value !== null && value !== undefined ? String(value) : ''); }}
                />
              </Tooltip>
            )}
          </span>
        </div>
      );
    }

    // markdown: бесшовное inline-редактирование на Tiptap. Сохранение по blur.
    if (isMarkdown) {
      const current = draft;
      const handleBlur = (md: string): void => {
        setEditing(false);
        const curr = value !== null && value !== undefined ? String(value) : '';
        if (md !== curr) onSave(md.trim() === '' ? null : md);
      };
      return (
        <div style={{ position: 'relative', width: '100%' }}>
          <MarkdownInlineEditor
            value={current}
            onChange={(next) => setDraft(next)}
            onBlur={handleBlur}
            placeholder="Добавьте описание…"
            onUploadImage={onUploadImage}
          />
          {onExpand && (
            <Tooltip title="Открыть в большом окне">
              <Button
                size="small"
                type="text"
                icon={<ExpandAltOutlined />}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => { onExpand(value !== null && value !== undefined ? String(value) : ''); }}
                style={{ position: 'absolute', top: 4, right: 6, zIndex: 1 }}
              />
            </Tooltip>
          )}
        </div>
      );
    }
    return (
      <div style={{ position: 'relative', width: '100%' }}>
        <Input.TextArea
          size="small"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          // Escape выходит из правки, СОХРАНЯЯ введённое, и не всплывает до
          // Modal карточки. В многострочное поле пишут абзацами — потерять их
          // по случайному Escape (как было: карточка закрывалась, текст исчезал)
          // дороже, чем не иметь отмены.
          onKeyDown={(e) => {
            if (e.key === 'Escape') { e.stopPropagation(); commit(); }
          }}
          rows={3}
          autoSize={false}
          placeholder="Введите текст…"
          autoFocus
          // [#шрифты] Наследуем размер/интерлиньяж от контекста поля (тело 14px,
          // лид 18px), иначе size="small" даёт 13px и текст «прыгает» относительно
          // read-вида (renderFieldValue при чтении = 14px).
          style={{ paddingRight: 28, fontSize: 'inherit', lineHeight: 'inherit' }}
        />
        {onExpand && (
          <Tooltip title="Открыть в большом окне">
            <Button
              size="small"
              type="text"
              icon={<ExpandAltOutlined />}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => { onExpand(value !== null && value !== undefined ? String(value) : ''); }}
              style={{ position: 'absolute', top: 2, right: 2 }}
            />
          </Tooltip>
        )}
      </div>
    );
  }

  // Text / number / url
  if (editing) {
    return (
      <Input
        size="small"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        // stopPropagation на Escape — иначе событие всплывает до Modal карточки
        // и закрывает её целиком (см. Escape в EditableHeaderTitle).
        onKeyDown={(e) => {
          if (e.key === 'Enter') commit();
          if (e.key === 'Escape') { e.stopPropagation(); setEditing(false); }
        }}
        autoFocus
        // [#шрифты] Наследуем размер поля (read = renderFieldValue), чтобы
        // size="small" не давал 13px там, где значение читается как 14px.
        style={{ fontSize: 'inherit' }}
      />
    );
  }

  return (
    <div onClick={startEdit} className={getEditableFieldClassName('text')}>
      <span className={styles.fieldValueInline}>
        {renderFieldValue(value, field, resolvedRefs)}
        {unit != null && value !== null && value !== undefined && (
          <span className={styles.fieldUnit}>{unit}</span>
        )}
      </span>
    </div>
  );
}

// ─── Item meta footer (created_at / updated_at / created_by) ────────────

export function ItemMetaFooter({ item }: { item: ItemRow }): JSX.Element {
  const author = (item as { createdByDisplayName?: string | null }).createdByDisplayName ?? null;
  const createdAt = item.createdAt ? new Date(item.createdAt).toLocaleString('ru-RU') : null;
  const updatedAt = item.updatedAt ? new Date(item.updatedAt).toLocaleString('ru-RU') : null;
  return (
    <div className={styles.metaFooter}>
      <Space split={<span style={{ color: 'var(--color-border)' }}>·</span>} size="small" wrap>
        {author && <span>Создал: <Text style={{ fontSize: 12 }}>{author}</Text></span>}
        {createdAt && <span>Создано: {createdAt}</span>}
        {updatedAt && updatedAt !== createdAt && <span>Обновлено: {updatedAt}</span>}
      </Space>
    </div>
  );
}

// ─── Jira sync section ───────────────────────────────────────────────────

function formatJiraError(err: unknown, fallback: string): React.ReactNode {
  const parts: string[] = [];
  let requestLabel = '';
  let requestBody: string | undefined;
  let responseBody: string | undefined;
  if (err instanceof ApiError) {
    const body = err.body as {
      jiraErrors?: unknown;
      httpStatus?: unknown;
      jiraRequestMethod?: unknown;
      jiraRequestPath?: unknown;
      jiraRequestBody?: unknown;
      jiraResponseBody?: unknown;
    };
    const msg = (err.message ?? '').trim();
    if (msg) parts.push(msg);
    const jiraErrors = body.jiraErrors;
    if (Array.isArray(jiraErrors)) {
      for (const e of jiraErrors) {
        if (typeof e === 'string' && e.trim()) parts.push(e.trim());
      }
    } else if (jiraErrors && typeof jiraErrors === 'object') {
      for (const v of Object.values(jiraErrors as Record<string, unknown>)) {
        if (typeof v === 'string' && v.trim()) parts.push(v.trim());
      }
    }
    if (typeof body.httpStatus === 'number' && parts.length === 0) {
      parts.push(`Jira ответила HTTP ${body.httpStatus}`);
    }
    if (typeof body.jiraRequestMethod === 'string' && typeof body.jiraRequestPath === 'string') {
      requestLabel = `${body.jiraRequestMethod} ${body.jiraRequestPath}`;
    }
    if (typeof body.jiraRequestBody === 'string') requestBody = body.jiraRequestBody;
    if (typeof body.jiraResponseBody === 'string') responseBody = body.jiraResponseBody;
  } else if (err instanceof Error) {
    const msg = err.message.trim();
    if (msg) parts.push(msg);
  } else if (typeof err === 'string' && err.trim()) {
    parts.push(err.trim());
  }
  const joined = parts.join(' — ');
  const main = joined.length > 0 ? joined : fallback;
  if (!requestLabel && !requestBody && !responseBody) {
    return main;
  }
  return (
    <div style={{ textAlign: 'left', maxWidth: 560 }}>
      <div>{main}</div>
      {requestLabel && (
        <div style={{ fontSize: 12, color: 'var(--color-text-secondary)', marginTop: 4 }}>
          Запрос: <code>{requestLabel}</code>
        </div>
      )}
      {requestBody && (
        <details style={{ marginTop: 4 }}>
          <summary style={{ fontSize: 12, color: 'var(--color-text-secondary)', cursor: 'pointer' }}>Тело запроса</summary>
          <pre style={{ fontSize: 11, whiteSpace: 'pre-wrap', wordBreak: 'break-all', maxHeight: 180, overflow: 'auto', margin: '4px 0 0' }}>{requestBody}</pre>
        </details>
      )}
      {responseBody && (
        <details style={{ marginTop: 4 }}>
          <summary style={{ fontSize: 12, color: 'var(--color-text-secondary)', cursor: 'pointer' }}>Тело ответа Jira</summary>
          <pre style={{ fontSize: 11, whiteSpace: 'pre-wrap', wordBreak: 'break-all', maxHeight: 180, overflow: 'auto', margin: '4px 0 0' }}>{responseBody}</pre>
        </details>
      )}
    </div>
  );
}

function JiraSection({ item, spaceId, fields, myRole, onItemUpdated }: { item: ItemRow; spaceId: string; fields: FieldDefinition[]; myRole?: string; onItemUpdated?: () => void }) {
  const canEdit = myRole !== 'viewer';
  const [refreshing, setRefreshing] = useState(false);
  const [jiraKey, setJiraKey] = useState<string | null>(null);
  const [jiraStatus, setJiraStatus] = useState<string | null>(null);
  const [compareOpen, setCompareOpen] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkInput, setLinkInput] = useState('');
  const [linking, setLinking] = useState(false);
  const [unlinking, setUnlinking] = useState(false);
  const [snapshot, setSnapshot] = useState<{
    syncConfigId: string | null;
    displayFields: string[];
    fields: JiraSnapshotField[];
    allAvailableFields: JiraSnapshotField[];
  } | null>(null);
  const [snapshotLoading, setSnapshotLoading] = useState(false);
  const [snapshotError, setSnapshotError] = useState<string | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [selectedFields, setSelectedFields] = useState<string[]>([]);
  const [savingSettings, setSavingSettings] = useState(false);

  useEffect(() => {
    setJiraKey(item.jiraIssueKey ?? null);
    setJiraStatus(item.jiraStatus ?? null);
  }, [item.id, item.jiraIssueKey, item.jiraStatus]);

  const titleSlug = resolveTitleField(fields)?.slug;
  const jiraTitle = titleSlug
    ? ((item.fields[titleSlug] as string) ?? null)
    : ((item.fields.title as string) ?? (item.fields.name as string) ?? null);

  const loadSnapshot = useCallback(async () => {
    setSnapshotLoading(true);
    setSnapshotError(null);
    try {
      const data = await fetchJiraSnapshot(spaceId, item.id);
      setSnapshot(data);
      setSelectedFields(data.displayFields);
    } catch (err: unknown) {
      const msg = typeof err === 'object' && err !== null && 'message' in err
        ? String((err as { message?: unknown }).message)
        : 'Ошибка загрузки данных из Jira';
      setSnapshotError(msg);
    } finally {
      setSnapshotLoading(false);
    }
  }, [spaceId, item.id]);

  useEffect(() => {
    if (jiraKey) void loadSnapshot();
    else setSnapshot(null);
  }, [jiraKey, loadSnapshot]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const result = await refreshItemJiraStatus(spaceId, item.id);
      setJiraStatus(result.jiraStatus ?? null);
      void message.success('Jira-статус обновлён');
      void loadSnapshot();
    } catch (err: unknown) {
      const code = typeof err === 'object' && err !== null && 'code' in err
        ? String((err as { code?: unknown }).code)
        : undefined;
      if (code === 'jira_token_not_set') {
        void message.error('Укажите Jira-токен в настройках профиля');
      } else {
        void message.error(formatJiraError(err, 'Ошибка обновления статуса'), 6);
      }
    } finally {
      setRefreshing(false);
    }
  };

  const handleLink = async () => {
    const key = linkInput.trim();
    if (!key) return;
    setLinking(true);
    try {
      const result = await linkItemToJira(spaceId, item.id, key);
      setJiraKey(result.jiraIssueKey);
      setJiraStatus(result.jiraStatus ?? null);
      setLinkOpen(false);
      setLinkInput('');
      void message.success(`Привязано к ${result.jiraIssueKey}`);
      onItemUpdated?.();
    } catch (err: unknown) {
      const code = typeof err === 'object' && err !== null && 'code' in err
        ? String((err as { code?: unknown }).code)
        : undefined;
      if (code === 'jira_token_not_set') {
        void message.error('Укажите Jira-токен в настройках профиля');
      } else {
        void message.error(formatJiraError(err, 'Не удалось привязать задачу'), 6);
      }
    } finally {
      setLinking(false);
    }
  };

  const handleUnlink = () => {
    Modal.confirm({
      title: 'Отвязать задачу Jira?',
      content: `Запись перестанет быть связанной с ${jiraKey}. Сама задача в Jira не удаляется.`,
      okText: 'Отвязать',
      okButtonProps: { danger: true },
      cancelText: 'Отмена',
      onOk: async () => {
        setUnlinking(true);
        try {
          await unlinkItemFromJira(spaceId, item.id);
          setJiraKey(null);
          setJiraStatus(null);
          setSnapshot(null);
          void message.success('Связь с Jira убрана');
          onItemUpdated?.();
        } catch (err: unknown) {
          void message.error(formatJiraError(err, 'Не удалось отвязать задачу'), 6);
        } finally {
          setUnlinking(false);
        }
      },
    });
  };

  const linkContent = (
    <div style={{ width: 240 }}>
      <div style={{ marginBottom: 8, fontWeight: 500, fontSize: 13 }}>Привязать к задаче Jira</div>
      <Input
        placeholder="Напр. DRTSVGOALS-8159"
        value={linkInput}
        onChange={(e) => setLinkInput(e.target.value)}
        onPressEnter={() => void handleLink()}
        autoFocus
        disabled={linking}
      />
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
        <Button size="small" onClick={() => setLinkOpen(false)} disabled={linking}>Отмена</Button>
        <Button size="small" type="primary" loading={linking} disabled={!linkInput.trim()} onClick={() => void handleLink()}>
          Привязать
        </Button>
      </div>
    </div>
  );

  const handleSaveSettings = async () => {
    if (!snapshot?.syncConfigId) return;
    setSavingSettings(true);
    try {
      await updateSyncConfig(spaceId, item.itemTypeId, { settings: { displayFields: selectedFields } });
      void message.success('Настройки сохранены');
      setSettingsOpen(false);
      void loadSnapshot();
    } catch {
      void message.error('Ошибка сохранения настроек');
    } finally {
      setSavingSettings(false);
    }
  };

  const settingsContent = (
    <div style={{ width: 280 }}>
      <div style={{ marginBottom: 8, fontWeight: 500, fontSize: 13 }}>Поля из Jira</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 300, overflowY: 'auto', marginBottom: 12 }}>
        {snapshotError && <span style={{ color: '#ff4d4f', fontSize: 12 }}>{snapshotError}</span>}
        {!snapshotError && (
          snapshot?.allAvailableFields?.length
            ? snapshot.allAvailableFields.map((f) => (
                <Checkbox
                  key={f.jiraKey}
                  checked={selectedFields.includes(f.jiraKey)}
                  onChange={(e) => setSelectedFields((prev) =>
                    e.target.checked ? [...prev, f.jiraKey] : prev.filter((k) => k !== f.jiraKey)
                  )}
                >
                  {f.label}
                </Checkbox>
              ))
            : <span style={{ color: 'var(--color-text-secondary)', fontSize: 12 }}>Нет данных из Jira</span>
        )}
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <Button size="small" onClick={() => setSettingsOpen(false)}>Отмена</Button>
        <Button size="small" type="primary" loading={savingSettings} onClick={() => void handleSaveSettings()}>
          Сохранить
        </Button>
      </div>
    </div>
  );

  return (
    <div className={styles.jiraCard} data-testid="jira-section">
      <div className={styles.jiraHeader}>
        <span className={styles.jiraTitle}>Jira</span>
        <div style={{ display: 'flex', gap: 4 }}>
          {jiraKey && (
            <>
              <Button
                size="small"
                icon={<ReloadOutlined spin={refreshing} />}
                loading={refreshing}
                onClick={() => void handleRefresh()}
                title="Обновить Jira-статус"
                data-testid="jira-refresh-button"
              />
              <Popover
                open={settingsOpen}
                onOpenChange={setSettingsOpen}
                trigger="click"
                content={settingsContent}
                placement="bottomRight"
                title={null}
              >
                <Button size="small" icon={<SettingOutlined />} title="Настроить отображаемые поля Jira" />
              </Popover>
            </>
          )}
          <Button
            size="small"
            onClick={() => setCompareOpen(true)}
            data-testid="jira-compare-button"
            title="Сравнить запись с Jira-задачей"
          >
            Сравнить
          </Button>
          {!jiraKey && canEdit && (
            <Popover
              open={linkOpen}
              onOpenChange={setLinkOpen}
              trigger="click"
              content={linkContent}
              placement="bottomRight"
              title={null}
            >
              <Button
                size="small"
                icon={<LinkOutlined />}
                data-testid="jira-link-button"
                title="Привязать к существующей задаче Jira"
              >
                Привязать
              </Button>
            </Popover>
          )}
          {jiraKey && canEdit && (
            <Button
              size="small"
              danger
              loading={unlinking}
              onClick={handleUnlink}
              data-testid="jira-unlink-button"
              title="Отвязать задачу Jira (задача в Jira не удаляется)"
            >
              Отвязать
            </Button>
          )}
        </div>
      </div>
      {jiraKey && (
        <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <JiraIssueLink jiraKey={jiraKey} summary={jiraTitle} />
          {jiraStatus && <Tag>{jiraStatus}</Tag>}
        </div>
      )}
      {jiraKey && (
        <div style={{ marginTop: 10 }}>
          {snapshotLoading && <span style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>Загрузка полей из Jira…</span>}
          {!snapshotLoading && snapshotError && (
            <span style={{ fontSize: 12, color: '#ff4d4f' }}>{snapshotError}</span>
          )}
          {!snapshotLoading && !snapshotError && snapshot && (() => {
            const visibleFields = snapshot.fields.filter((f) => f.jiraKey !== 'summary' && f.jiraKey !== 'status');
            if (visibleFields.length === 0) return null;
            return (
              <div style={{ marginTop: 8 }}>
                {visibleFields.map((f, i) => (
                  <div
                    key={f.jiraKey}
                    className={styles.fieldRow}
                    style={i === visibleFields.length - 1 ? { borderBottom: 'none' } : undefined}
                  >
                    <span className={styles.fieldLabel}>{f.label}</span>
                    <div className={styles.fieldValueCell}>
                      <span style={{ fontSize: 13, padding: '10px 0', display: 'inline-block', color: f.value ? undefined : 'var(--color-text-secondary)' }}>
                        {f.value ?? '—'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            );
          })()}
        </div>
      )}
      <JiraCompareModal
        open={compareOpen}
        onClose={() => setCompareOpen(false)}
        spaceId={spaceId}
        itemId={item.id}
        jiraIssueKey={jiraKey}
        onApplied={onItemUpdated}
      />
    </div>
  );
}

// ─── Draggable tabs ───────────────────────────────────────────────────

function SortableTab({ id, children }: { id: string; children: React.ReactNode }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform ? { ...transform, scaleY: 1 } : null),
    transition,
    opacity: isDragging ? 0.5 : 1,
    cursor: 'grab',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
  };
  return (
    <span ref={setNodeRef} className={styles.sortableTab} style={style} {...attributes} {...listeners}>
      <HolderOutlined className={styles.dragHandle} style={{ fontSize: 10, color: 'var(--color-border)' }} />
      {children}
    </span>
  );
}

function DraggableTabs({
  items,
  tabOrder,
  onTabOrderChange,
  defaultActiveKey,
}: {
  items: Array<{ key: string; label: React.ReactNode; children: React.ReactNode }>;
  tabOrder?: string[];
  onTabOrderChange: (order: string[]) => void;
  /** Какую вкладку открыть первой; по умолчанию — первую по порядку. */
  defaultActiveKey?: string;
}) {
  const tabSensor = useSensor(PointerSensor, { activationConstraint: { distance: 10 } });
  const tabSensors = useSensors(tabSensor);

  // Sort tabs by saved order
  const sortedItems = useMemo(() => {
    if (!tabOrder || tabOrder.length === 0) return items;
    const orderMap = new Map(tabOrder.map((key, idx) => [key, idx]));
    return [...items].sort((a, b) => {
      const ia = orderMap.get(a.key) ?? 999;
      const ib = orderMap.get(b.key) ?? 999;
      return ia - ib;
    });
  }, [items, tabOrder]);

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const keys = sortedItems.map((i) => i.key);
    const oldIndex = keys.indexOf(String(active.id));
    const newIndex = keys.indexOf(String(over.id));
    if (oldIndex === -1 || newIndex === -1) return;
    const newOrder = arrayMove(keys, oldIndex, newIndex);
    onTabOrderChange(newOrder);
  }, [sortedItems, onTabOrderChange]);

  const tabsWithDrag = sortedItems.map((item) => ({
    ...item,
    label: <SortableTab id={item.key}>{item.label}</SortableTab>,
  }));

  return (
    <DndContext sensors={tabSensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={sortedItems.map((i) => i.key)} strategy={horizontalListSortingStrategy}>
        <Tabs items={tabsWithDrag} defaultActiveKey={defaultActiveKey ?? sortedItems[0]?.key ?? 'comments'} tabBarGutter={20} data-testid="item-drawer-tabs" />
      </SortableContext>
    </DndContext>
  );
}

export function ItemDrawer({
  open,
  onClose,
  item,
  fields,
  spaceId,
  spaceSlug,
  itemTypeId,
  currentUserId,
  myRole,
  jiraEnabled = false,
  hermesEnabled = false,
  resolvedRefs,
  onFieldSave,
  onDelete,
  onDuplicate,
  onLinkedItemClick,
  availableTypes,
  onTypeChanged,
  onNavigate,
  navPosition,
}: ItemDrawerProps): JSX.Element {
  const mainFields = fields;
  const allSlugs = useMemo(() => mainFields.map((f) => f.slug), [mainFields]);
  const drawerSettings = useDrawerSettings(
    `${spaceId}:${itemTypeId ?? 'default'}`,
    allSlugs,
  );
  const visibleFields = useMemo(
    () => mainFields.filter((field) => drawerSettings.visibleSlugs.includes(field.slug)),
    [mainFields, drawerSettings.visibleSlugs],
  );

  // Расширенный режим (width >= 1300) — карточка на ВЕСЬ экран: 100vw×100vh,
  // без полей и скруглений (см. .fullscreenModal в CSS). Хранимая ширина
  // остаётся числом (1400) — это переключатель режима, а не реальная ширина.
  const isFullscreen = drawerSettings.settings.width >= 1300;
  // [card-layout] Скелет «Лента записи»: лид-поле + свойства во всю ширину +
  // вкладки, без отдельной title-шапки и без зарезервированной пустоты.
  // Теперь ВСЕГДА (фича-тогл убран); классический двухпанельник — dead-код,
  // остаётся до отдельной чистки.
  const streamMode = true;

  // Живой размер верхней панели сплиттера (описание+свойства). Во время
  // перетаскивания храним пиксельный размер (гладкий контролируемый драг),
  // а в localStorage сохраняем проценты только по завершении — чтобы не
  // молотить запись на каждый пиксель.
  //
  // [content-first] Пока пользователь сам не тянул сплиттер (topSize не
  // сохранён) — высота верхней панели АВТО: меряется по фактическому контенту
  // (описание + свойства) с потолком 68% карточки. Карточка не резервирует
  // место под пустоту — всё свободное сразу уходит вкладкам. Перетаскивание
  // персистится и становится оверрайдом; «Сбросить настройки» возвращает авто.
  // Замер — в layout-эффекте ниже (после объявления expandedField).
  const persistedTopSize = drawerSettings.settings.topSize ?? null;
  const [liveTopSize, setLiveTopSize] = useState<number | string>(
    persistedTopSize != null ? `${persistedTopSize}%` : 220,
  );
  // [адаптивная высота] В оконном авто-режиме высота модалки тоже по
  // контенту: верх (по замеру) + фиксированный минимум под вкладки. Минимум
  // фиксированный сознательно — высота НЕ зависит от контента активной
  // вкладки, иначе карточка прыгала бы при переключении вкладок.
  // null — некомпактный режим (fullscreen или пользовательский topSize).
  // Высота тела в оконном режиме постоянная (см. styles.body ниже); setter
  // остался только у мёртвой классической ветки.
  const [, setAutoBodyHeight] = useState<number | null>(null);
  // Элементы для замера — через callback-ref в state, а не useRef: Modal
  // монтирует контент в портал ПОЗЖЕ первого layout-эффекта (и пересоздаёт
  // его при каждом открытии из-за destroyOnHidden). useRef не вызывает
  // перезапуск эффекта, и замер молча не стартует; state-ref перезапускает.
  // recRoot-ref сохраняем (ref на корень карточки), но само значение больше не
  // читаем: авто-высота считается от стабильного окна, а не от recRoot (anti-jerk).
  const [, setRecRootEl] = useState<HTMLDivElement | null>(null);
  const [topContentEl, setTopContentEl] = useState<HTMLDivElement | null>(null);

  // Поле-заголовок карточки: его значение выносится в шапку, поэтому ни в
  // «Свойствах», ни в теле оно дублироваться не должно. Приоритет (единый
  // резолвер): config.isTitle → slug title/name → первое текстовое поле по
  // sortOrder. Определяется ВЫШЕ bodyFields, т.к. заголовок типа textarea
  // относится к зоне body и его нужно исключить из тела (иначе два редактора
  // на одно значение — в шапке inline и в теле).
  const titleField = useMemo(() => resolveTitleField(mainFields), [mainFields]);

  // [title dedup] Заголовок дублируется только для текстовых полей — они
  // полностью редактируются в шапке/теле. reference-заголовок в шапке read-only
  // (это dictionary-picker), поэтому его НЕ исключаем, иначе значение стало бы
  // недоступно для правки.
  const titleDedupSlug =
    titleField && titleField.fieldType !== 'reference' ? titleField.slug : undefined;

  // Тело записи — ВСЕ markdown/textarea-поля, в исходном порядке, столбиком
  // слева (кроме поля-заголовка: оно рендерится в шапке). Остальные поля идут
  // в компактную боковую колонку справа («Свойства»).
  const bodyFields = useMemo(
    () => visibleFields.filter((f) => fieldZone(f) === 'body' && f.slug !== titleDedupSlug),
    [visibleFields, titleDedupSlug],
  );
  // Статусное поле (≤1 на тип) выносим в шапку пилюлей-состоянием — это
  // первое, что спрашивают про запись. В «Свойствах» оно больше не дублируется.
  const statusField = useMemo(
    () => visibleFields.find((f) => fieldZone(f) === 'identity') ?? null,
    [visibleFields],
  );
  // props-поля + защита: инвариант A1 гарантирует ≤1 статусного поля на тип,
  // но если на тип всё же просочилось второе (битый импорт/сидер), оно не
  // должно молча исчезать — лишние identity-кандидаты (кроме вынесенного в
  // шапку statusField) падают в «Свойства» и рендерятся обычным статус-чипом
  // (ветка statusChip в renderFieldCell — её safety-net, а не мёртвый код).
  const sideFields = useMemo(
    () => visibleFields.filter(
      (f) => fieldZone(f) === 'props' || (fieldZone(f) === 'identity' && f !== statusField),
    ),
    [visibleFields, statusField],
  );

  // Отображаемое значение поля-заголовка. Для reference резолвим entryId (или
  // массив) в display справочника через resolvedRefs — иначе в шапке и на
  // вкладке «Оценка» показался бы UUID (lens-pitfalls: raw id vs label).
  const titleDisplay = useMemo(() => {
    if (!titleField || !item) return '';
    const raw = item.fields[titleField.slug];
    if (titleField.fieldType === 'reference') {
      const toLabel = (v: unknown): string =>
        (v != null && resolvedRefs?.[String(v)]) || String(v ?? '');
      if (Array.isArray(raw)) return raw.map(toLabel).filter(Boolean).join(', ');
      return raw != null && raw !== '' ? toLabel(raw) : '';
    }
    return raw != null ? String(raw) : '';
  }, [titleField, item, resolvedRefs]);

  // [title dedup] Считаем набор полей боковой панели один раз, чтобы одно и то же
  // условие решало и рендер контента, и показ самой панели.
  const panelFields = useMemo(
    () => sideFields.filter((f) => f.slug !== titleDedupSlug),
    [sideFields, titleDedupSlug],
  );
  // Есть ли у типа props-поля, которые в принципе могут жить в «Свойствах»
  // (даже если сейчас скрыты через шестерёнку)? Если да — панель нужна, чтобы
  // юзер мог их вернуть. Если у типа НЕТ таких полей (только описание/статус/
  // заголовок в шапке) — панель справа пустая и её показывать не нужно.
  // titleField исключаем: text-поле-заголовок относится к зоне props, но
  // рендерится в шапке, а не в панели.
  const hasHideableSideFields = useMemo(
    () => mainFields.some((f) => fieldZone(f) === 'props' && f.slug !== titleDedupSlug),
    [mainFields, titleDedupSlug],
  );
  const showSidePanel = panelFields.length > 0 || hasHideableSideFields;

  const handleUploadImage = useCallback(
    async (file: File): Promise<string> => {
      try {
        const saved = await uploadAttachment(spaceId, file);
        return saved.url;
      } catch (err) {
        void message.error(
          err instanceof ApiError ? err.message : 'Не удалось загрузить изображение',
        );
        throw err;
      }
    },
    [spaceId],
  );

  const [showFieldSettings, setShowFieldSettings] = useState(false);
  // [density] Пустые текстовые блоки не занимают место — свёрнуты под
  // строку добавления; раскрытие живёт в пределах открытой карточки.
  const [openedEmptyBody, setOpenedEmptyBody] = useState<string[]>([]);
  // Два вида карточки: «Основное» — свойства, текст, обсуждение (с этим
  // работают); «Ещё» — связи, история, оценка, Lensy (справочное). «Ещё»
  // полностью заменяет тело карточки; на другой записи снова «Основное».
  // Что показывает правая колонка: обсуждение (по умолчанию) или один из
  // разделов записи — «Связи», «История», «Оценка», «Lensy». Слева всегда
  // сама запись: свойства и тексты. На другой записи снова обсуждение.
  const [rightTab, setRightTab] = useState<string>('comments');
  // Левая часть карточки (название, свойства, текст) свёрнута — правая панель
  // на всю ширину: на доске и в длинном обсуждении нужно место. Живёт, пока
  // открыта карточка, и сохраняется при листании записей.
  const [docCollapsed, setDocCollapsed] = useState(false);
  // Ширина колонки обсуждения, % от ширины карточки. Пользователь тянет
  // границу между текстом и обсуждением; значение одно на все карточки и
  // живёт в localStorage. null — ширина по умолчанию из CSS.
  const [talkPct, setTalkPct] = useState<number | null>(() => {
    try {
      const raw = localStorage.getItem(TALK_WIDTH_KEY);
      const n = raw == null ? NaN : Number(raw);
      return Number.isFinite(n) ? clampTalkPct(n) : null;
    } catch {
      return null;
    }
  });
  const colsRef = useRef<HTMLDivElement | null>(null);
  const startTalkResize = useCallback((e: ReactPointerEvent<HTMLDivElement>) => {
    const box = colsRef.current?.getBoundingClientRect();
    if (!box || box.width === 0) return;
    e.preventDefault();
    const handle = e.currentTarget;
    handle.setPointerCapture(e.pointerId);
    let last: number | null = null;
    const onMove = (ev: PointerEvent) => {
      last = clampTalkPct(((box.right - ev.clientX) / box.width) * 100);
      setTalkPct(last);
    };
    const onUp = () => {
      handle.removeEventListener('pointermove', onMove);
      handle.removeEventListener('pointerup', onUp);
      handle.removeEventListener('pointercancel', onUp);
      if (last != null) {
        try { localStorage.setItem(TALK_WIDTH_KEY, String(Math.round(last * 10) / 10)); } catch { /* квота */ }
      }
    };
    handle.addEventListener('pointermove', onMove);
    handle.addEventListener('pointerup', onUp);
    handle.addEventListener('pointercancel', onUp);
  }, []);
  const resetTalkWidth = useCallback(() => {
    setTalkPct(null);
    try { localStorage.removeItem(TALK_WIDTH_KEY); } catch { /* ignore */ }
  }, []);
  // Пустые поля свёрнуты под строкой «Ещё N пустых» — сознательно НЕ в
  // localStorage: желание раскрыть привязано к конкретной записи («хочу
  // заполнить эту»), а не к пользователю. Сбрасывается при смене записи.
  const [expandedField, setExpandedField] = useState<{
    slug: string;
    name: string;
    value: string;
    mode: 'markdown' | 'textarea';
  } | null>(null);

  // Черновик большого редактора («Открыть в большом окне»). Там сохранение по
  // кнопке, поэтому при закрытии карточки мимо неё — кликом по затемнённой зоне
  // или крестиком — набранное иначе пропадает: поддерево сносится синхронно.
  const expandedDraftRef = useRef<{ slug: string; text: string } | null>(null);

  useEffect(() => {
    if (!open) { expandedDraftRef.current = null; setExpandedField(null); }
  }, [open]);
  useEffect(() => { expandedDraftRef.current = null; setExpandedField(null); }, [item?.id]);
  // Свёрнутые пустые поля — состояние просмотра, а не настройка: на соседней
  // записи карточка снова открывается в компактном виде.
  useEffect(() => { setOpenedEmptyBody([]); setRightTab('comments'); }, [item?.id]);

  /**
   * Закрытие карточки: сначала дописываем всё несохранённое, потом закрываем.
   * Inline-поля коммитятся по blur — снимаем фокус явно, потому что клик по
   * маске его не уводит. Большое окно сохраняем из черновика.
   */
  const handleDrawerClose = useCallback((): void => {
    const active = document.activeElement;
    if (active instanceof HTMLElement) active.blur();
    const draft = expandedDraftRef.current;
    if (draft && onFieldSave && item) {
      const parsed = draft.text.trim() === '' ? null : draft.text;
      const current = item.fields[draft.slug] ?? null;
      if (parsed !== current) onFieldSave(draft.slug, parsed);
    }
    expandedDraftRef.current = null;
    onClose();
  }, [onClose, onFieldSave, item]);

  // Листание записей с клавиатуры: следующая — J или ↓/→, предыдущая — K или
  // ↑/←. J/K идут по e.code (vim-style, работает и в русской раскладке),
  // стрелки — по e.key. Игнорируем, когда фокус в любом редакторе
  // (input/textarea/tiptap/select) — там буквы печатают текст, а стрелки
  // двигают курсор, — и когда открыто меню, которое само живёт на стрелках.
  useEffect(() => {
    if (!open || !onNavigate) return;
    const handler = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey || e.shiftKey) return;
      // target может быть window/document (не Element) — instanceof, не каст
      const t = e.target instanceof HTMLElement ? e.target : null;
      if (
        t
        && (t.tagName === 'INPUT'
          || t.tagName === 'TEXTAREA'
          || t.isContentEditable
          || t.closest('.ant-select, .ant-picker, [contenteditable="true"]'))
      ) return;
      // Открытый Dropdown/меню рендерится в портале (фокус может остаться
      // на кнопке-триггере), поэтому проверяем не target, а наличие в DOM:
      // иначе стрелки листали бы записи вместо пунктов меню.
      if (document.querySelector('.ant-dropdown:not(.ant-dropdown-hidden)')) return;
      const forward = e.code === 'KeyJ' || e.key === 'ArrowDown' || e.key === 'ArrowRight';
      const back = e.code === 'KeyK' || e.key === 'ArrowUp' || e.key === 'ArrowLeft';
      if (forward) {
        e.preventDefault();
        onNavigate(1);
      } else if (back) {
        e.preventDefault();
        onNavigate(-1);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onNavigate]);

  // [content-first] Авто-высота верхней панели: меряем контент (recLayout),
  // пока нет пользовательского оверрайда. ResizeObserver ловит рост контента
  // (набор текста в описании, раскрытие пустых полей / сворачивание секций),
  // ресайз окна и анимацию открытия модалки (root 0 → полная высота). Петля
  // обратной связи разорвана В ДВУХ местах: (1) высоту панели меряем от
  // СТАБИЛЬНОГО окна, а не от recRoot (см. ниже); (2) `.recTopPane` имеет
  // `scrollbar-gutter: stable` — иначе появление/исчезновение полосы прокрутки
  // при смене высоты панели сужало бы контент, менялся бы offsetHeight, RO
  // пересчитывал бы высоту → полоса опять переключается → бесконечное дёрганье
  // на классических скроллбарах Win/Linux (см. коммент у .recTopPane в CSS).
  // НЕ УБИРАТЬ ни то, ни другое.
  //
  // В оконном режиме заодно считаем адаптивную высоту модалки: верх + минимум
  // вкладок (BOTTOM_PANE_MIN) + футер/сплиттер (DRAWER_CHROME). Потолок верха
  // здесь считается от максимума окна (100vh-180), а не от recRoot — высота
  // recRoot сама зависит от результата, завязка на неё дала бы петлю.
  useLayoutEffect(() => {
    // [content-first] persistedTopSize (ручной сплиттер) — это ПОТОЛОК доли, а не
    // жёсткая высота: и в stream, и в классике верх всегда не больше фактического
    // контента (см. compute). Юзер может ужать верх, но не раздуть его в пустоту —
    // разреженная запись (пустое тело, пара полей) не даёт «дыру» на пол-экрана.
    // Без ручного оверрайда потолок доли — 68%.
    if (!topContentEl) return;
    // [anti-jerk] Считаем «потолок» верха от СТАБИЛЬНОЙ высоты окна, а не от
    // recRootEl: во время zoom-анимации открытия модалки clientHeight recRoot
    // растёт 0→full, и завязка на него дёргала размер панели каждый кадр.
    // offsetHeight контента при этом стабилен (scale-трансформа не меняет
    // layout-высоту), поэтому recRootEl больше не наблюдаем и не читаем.
    const compute = () => {
      const desired = topContentEl.offsetHeight + 14;
      if (isFullscreen) {
        const available = window.innerHeight - 120;
        const next = Math.round(Math.max(96, Math.min(desired, available * 0.68)));
        setAutoBodyHeight((p) => (p === null ? p : null));
        setLiveTopSize((p) => (p === next ? p : next));
      } else if (streamMode) {
        // [card-flow] Высоту больше не назначаем: карточка — один поток,
        // модалка растёт по контенту до потолка в CSS. Раньше здесь тело
        // раздувалось до высоты окна «про запас под ленту», из-за чего
        // короткая запись открывалась экраном пустоты под вкладками.
        setAutoBodyHeight((p) => (p === null ? p : null));
      } else {
        // Классика (двухпанельник) — dead-код, оставлен до отдельной чистки.
        const maxBody = window.innerHeight - 180;
        // [no-inner-scroll] В stream верх подгоняем ПОД КОНТЕНТ (не режем на 68%),
        // оставляя вкладкам лишь скромный минимум — тогда у верхней панели нет
        // своей полосы прокрутки: описание видно целиком, а не в мини-скролле.
        // Внутренний скролл включается только если контент реально не влезает
        // (очень длинное описание) — тогда верх упирается в потолок. Классика
        // сохраняет прежний потолок 68% (двухпанельный верх и так компактный).
        // 420 — вкладке «Комментарии» на короткой записи хватает на «Саммари»,
        // ленту из нескольких реплик и композер без тесноты (260 давало лишь
        // пару строк ленты — комментарии было не читать); длинный тред всё
        // равно скроллится, это неизбежно.
        const minTabs = streamMode ? 420 : BOTTOM_PANE_MIN;
        // Классика: потолок доли = ручной сплиттер (persisted %) либо дефолт 68%.
        // Верх всё равно подгоняется под контент (Math.min с desired ниже) — так
        // «потянул вниз» ужимает верх, а короткая запись не оставляет пустоту.
        const classicCap = persistedTopSize != null ? (persistedTopSize / 100) * maxBody : maxBody * 0.68;
        const topPx = Math.round(Math.max(96, Math.min(desired, classicCap)));
        setLiveTopSize((p) => (p === topPx ? p : topPx));
        const bodyH = Math.min(maxBody, Math.max(MIN_BODY_HEIGHT, topPx + minTabs + DRAWER_CHROME));
        setAutoBodyHeight((p) => (p === bodyH ? p : bodyH));
      }
    };
    // Первый замер — синхронно в layout-эффекте (до paint), чтобы панель сразу
    // была нужного размера, без вспышки стартовых 220px. Последующие пересчёты
    // (RO/resize) коалесцируем в один кадр через rAF — burst во время анимации
    // не превращается в череду ре-рендеров.
    let rafId = 0;
    const schedule = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => { rafId = 0; compute(); });
    };
    compute();
    const ro = new ResizeObserver(schedule);
    ro.observe(topContentEl);
    window.addEventListener('resize', schedule);
    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      ro.disconnect();
      window.removeEventListener('resize', schedule);
    };
    // item?.id — пересчитать высоту синхронно при переключении записей
    // (стрелки/список): контент новой записи иной, а topContentEl тот же
    // элемент, поэтому без этого dep эффект не перезапускался и верх оставался
    // от прошлой карточки (не подгонялся → внутренний скролл).
  }, [persistedTopSize, topContentEl, isFullscreen, streamMode, item?.id]);
  const [linkRules, setLinkRules] = useState<LinkRule[] | null>(null);
  const [applyingLinkRuleId, setApplyingLinkRuleId] = useState<string | null>(null);
  const [changingType, setChangingType] = useState(false);
  const [tabCounts, setTabCounts] = useState<{ comments: number | null; linked: number | null; history: number | null }>({
    comments: null,
    linked: null,
    history: null,
  });
  const isAdmin = myRole === 'admin';

  useEffect(() => {
    if (!open || !isAdmin || !itemTypeId) return;
    let cancelled = false;
    void (async () => {
      try {
        const rules = await listLinkRules(spaceId, itemTypeId);
        if (!cancelled) setLinkRules(rules);
      } catch {
        if (!cancelled) setLinkRules([]);
      }
    })();
    return () => { cancelled = true; };
  }, [open, isAdmin, itemTypeId, spaceId]);

  useEffect(() => {
    if (!open || !item) return;
    let cancelled = false;
    setTabCounts({ comments: null, linked: null, history: null });
    void (async () => {
      const [comments, linked, history] = await Promise.all([
        listItemComments(spaceId, item.id).then((r) => r.comments.length).catch(() => null),
        apiRequest<{ items: unknown[] }>(`/api/spaces/${spaceId}/items/${item.id}/links`)
          .then((r) => r.items.length)
          .catch(() => null),
        getItemHistory(spaceId, item.id).then((r) => r.entries.length).catch(() => null),
      ]);
      if (!cancelled) setTabCounts({ comments, linked, history });
    })();
    return () => { cancelled = true; };
  }, [open, spaceId, item?.id]);

  if (!item) {
    return <Modal open={false} onCancel={onClose} footer={null} />;
  }

  const canEdit = myRole !== 'viewer';

  const fieldSettingsContent = (
    <div style={{ maxHeight: 320, overflow: 'auto', minWidth: 220 }}>
      {mainFields.map((f) => {
        const hidden = drawerSettings.settings.hiddenFields.includes(f.slug);
        return (
          <div key={f.slug} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '3px 0' }}>
            <Checkbox
              checked={!hidden}
              onChange={() => drawerSettings.toggleField(f.slug)}
            />
            <span style={{ flex: 1, opacity: hidden ? 0.4 : 1 }}>{f.name}</span>
          </div>
        );
      })}
      <div style={{ borderTop: '1px solid var(--color-border)', marginTop: 8, paddingTop: 8 }}>
        <Button size="small" type="link" icon={<UndoOutlined />} onClick={drawerSettings.resetSettings}>
          Показать все
        </Button>
      </div>
    </div>
  );

  const renderTabLabel = (text: string, count: number | null) => (
    <>
      {text}
      {count !== null && count > 0 && (
        <span
          className={styles.tabCount}
          aria-label={`${text}: ${count}`}
        >
          {count}
        </span>
      )}
    </>
  );

  const tabItems = [
    {
      key: 'comments',
      label: renderTabLabel('Комментарии', tabCounts.comments),
      children: (
        // flex-колонка на всю высоту вкладки — продолжение цепочки
        // .ant-tabs-tabpane-active → CommentSection, чтобы композер был
        // прибит к низу зоны вкладок даже при пустой ленте.
        <div style={{ display: 'flex', flexDirection: 'column', flex: '1 1 auto', minHeight: 0 }}>
          <CommentSection
            spaceId={spaceId}
            itemId={item.id}
            currentUserId={currentUserId}
            canEdit={canEdit}
            headerLeft={
              // [#10] Саммари обсуждения нечего показывать, пока комментариев
              // нет — не выводим кнопку при пустом треде (убираем ложный
              // affordance). Живёт в одной строке с переключателем порядка.
              canEdit && (tabCounts.comments ?? 0) > 0 ? (
                <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                  <AiSuggestButton
                    itemId={item.id}
                    kind="summarize_comments"
                    tooltip="Lensy: саммари обсуждения"
                  />
                  <Text type="secondary" style={{ fontSize: 12, marginLeft: 4 }}>
                    Саммари обсуждения
                  </Text>
                </span>
              ) : undefined
            }
          />
        </div>
      ),
    },
    {
      key: 'linked',
      label: renderTabLabel('Связи', tabCounts.linked),
      children: (
        <LinkedItemsTable
          spaceId={spaceId}
          itemId={item.id}
          jiraIssueKey={item.jiraIssueKey ?? undefined}
          canEdit={canEdit}
          onItemClick={(linkedId) => {
            if (onLinkedItemClick) onLinkedItemClick(linkedId, spaceId);
          }}
        />
      ),
    },
    {
      key: 'board',
      label: renderTabLabel('Доска', null),
      children: (
        <ItemWhiteboard
          spaceId={spaceId}
          spaceSlug={spaceSlug}
          itemId={item.id}
          canEdit={canEdit}
          jiraIssueKey={jiraEnabled ? item.jiraIssueKey : null}
        />
      ),
    },
    {
      key: 'history',
      label: renderTabLabel('История', tabCounts.history),
      children: <AuditHistory spaceId={spaceId} itemId={item.id} />,
    },
    ...(canEdit && jiraEnabled
      ? [
          {
            key: 'jira',
            label: renderTabLabel('Jira', item.jiraIssueKey ? 1 : null),
            children: <JiraSection item={item} spaceId={spaceId} fields={fields} myRole={myRole} />,
          },
        ]
      : []),
    ...(hermesEnabled
      ? [
          {
            key: 'hermes',
            label: renderTabLabel('Оценка', null),
            children: (
              <EstimateRecordDrawer
                spaceId={spaceId}
                itemId={item.id}
                myRole={myRole}
                itemKey={(item as { displayKey?: string | null }).displayKey ?? undefined}
                itemTitle={titleDisplay || undefined}
                onLaunchInteractive={onClose}
              />
            ),
          },
        ]
      : []),
    ...(canEdit
      ? [
          {
            key: 'lensy',
            label: 'Lensy',
            children: <ItemAiChat itemId={item.id} />,
          },
        ]
      : []),
  ];

  return (
    <Modal
      title={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, flex: 1 }}>
            {(item as { displayKey?: string | null }).displayKey && (
              <>
                <Tag
                  style={{
                    margin: 0,
                    fontFamily: 'monospace',
                    cursor: 'copy',
                    // Ключ записи — служебная пометка, а не акцент: серый
                    // моноширинный текст без плашки. Цветная подложка делала
                    // его самым заметным объектом шапки, хотя читают название.
                    background: 'none',
                    color: 'var(--color-text-secondary)',
                    borderColor: 'transparent',
                    padding: 0,
                    fontSize: 11,
                    letterSpacing: '0.04em',
                  }}
                  onClick={() => {
                    void navigator.clipboard?.writeText((item as { displayKey?: string }).displayKey ?? '');
                    void message.success('Ключ скопирован');
                  }}
                >
                  {(item as { displayKey?: string }).displayKey}
                </Tag>
                {spaceSlug && (
                  <Tooltip title="Копировать короткую ссылку на карточку">
                    <Button
                      size="small"
                      type="text"
                      icon={<LinkOutlined />}
                      onClick={() => {
                        const key = (item as { displayKey?: string }).displayKey ?? '';
                        const url = `${window.location.origin}/c/${encodeURIComponent(spaceSlug)}/${encodeURIComponent(key)}`;
                        void navigator.clipboard?.writeText(url);
                        void message.success('Ссылка скопирована');
                      }}
                    />
                  </Tooltip>
                )}
              </>
            )}
            {/* [card-layout] В stream-режиме отдельной title-строки нет: поле-
                заголовок рендерится лид-блоком в теле, а идентичность держит
                displayKey-чип. Дубль (эхо в шапке + редактор в теле) исчезает. */}
            {!streamMode && (() => {
              // Заголовок шапки: значение поля-заголовка (title/name/первое
              // текстовое по sortOrder) → человекочитаемый displayKey → 'Запись'.
              // Берём именно значение, не сырой id.
              const titleStr = titleDisplay.trim();
              const dk = (item as { displayKey?: string | null }).displayKey;
              const fallback = titleStr || (dk ? dk : 'Запись');
              // Inline-правка доступна только для text/textarea-заголовка:
              // markdown-заголовок остаётся редактируемым в теле карточки, а
              // displayKey/'Запись' — это не значение поля, его не правим.
              const canEditTitle =
                !!onFieldSave
                && canEdit
                && !!titleField
                && isFieldEditable(titleField)
                && (titleField.fieldType === 'text' || titleField.fieldType === 'textarea');
              return (
                <EditableHeaderTitle
                  value={titleStr}
                  placeholder={fallback}
                  editable={canEditTitle}
                  onSave={(next) => { if (titleField) onFieldSave?.(titleField.slug, next); }}
                />
              );
            })()}
            {statusField && (
              <span className={styles.headerStatus}>
                {onFieldSave && canEdit ? (
                  <DrawerField
                    value={item.fields[statusField.slug] ?? null}
                    field={statusField}
                    resolvedRefs={resolvedRefs}
                    spaceId={spaceId}
                    statusChip={renderStatusChip(statusField, item.fields[statusField.slug])}
                    onSave={(val) => onFieldSave(statusField.slug, val)}
                  />
                ) : (
                  renderStatusChip(statusField, item.fields[statusField.slug])
                )}
              </span>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginRight: 32 }}>
            {onNavigate && navPosition && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
                <Tooltip title="Предыдущая запись (↑, ← или K)">
                  <Button
                    size="small"
                    type="text"
                    icon={<UpOutlined />}
                    disabled={navPosition.index <= 0}
                    onClick={() => onNavigate(-1)}
                    data-testid="drawer-nav-prev"
                  />
                </Tooltip>
                <span
                  style={{
                    fontSize: 11,
                    color: 'var(--color-text-secondary)',
                    fontVariantNumeric: 'tabular-nums',
                    whiteSpace: 'nowrap',
                  }}
                  data-testid="drawer-nav-counter"
                >
                  {navPosition.index + 1} / {navPosition.total}
                </span>
                <Tooltip title="Следующая запись (↓, → или J)">
                  <Button
                    size="small"
                    type="text"
                    icon={<DownOutlined />}
                    disabled={navPosition.index >= navPosition.total - 1}
                    onClick={() => onNavigate(1)}
                    data-testid="drawer-nav-next"
                  />
                </Tooltip>
              </span>
            )}
            <Tooltip title={isFullscreen ? 'Свернуть в окно' : 'На весь экран'}>
              <Button
                size="small"
                type="text"
                icon={isFullscreen ? <ShrinkOutlined /> : <ExpandAltOutlined />}
                onClick={() => drawerSettings.setWidth(isFullscreen ? COMPACT_WIDTH : 1400)}
                data-testid="drawer-width-toggle"
              />
            </Tooltip>
            {(() => {
              const hasLinkRules = isAdmin && itemTypeId && linkRules && linkRules.length > 0;
              const canDuplicate = !!onDuplicate && canEdit;
              const canDelete = !!onDelete && canEdit;
              const otherTypes = (availableTypes ?? []).filter((t) => t.id !== item.itemTypeId);
              const canChangeType = canEdit && otherTypes.length > 0;

              const menuItems: Array<{
                key: string;
                label: React.ReactNode;
                icon?: React.ReactNode;
                danger?: boolean;
                onClick?: () => void;
                children?: Array<{ key: string; label: string; onClick: () => void }>;
                type?: 'divider';
              }> = [];

              if (hasLinkRules) {
                menuItems.push({
                  key: 'apply-rules',
                  icon: <ThunderboltOutlined />,
                  label: 'Связать',
                  children: linkRules!.map((rule) => ({
                    key: rule.id,
                    label: rule.name,
                    onClick: () => {
                      Modal.confirm({
                        title: `Связать по правилу «${rule.name}»?`,
                        okText: 'Связать',
                        cancelText: 'Отмена',
                        onOk: async () => {
                          try {
                            setApplyingLinkRuleId(rule.id);
                            const result = await applyLinkRuleBulk(spaceId, itemTypeId!, rule.id, {
                              itemIds: [item.id],
                            });
                            void message.success(`Создано связей: ${result.linksCreated}`);
                          } catch (err) {
                            void message.error(err instanceof Error ? err.message : 'Ошибка применения правила');
                          } finally {
                            setApplyingLinkRuleId(null);
                          }
                        },
                      });
                    },
                  })),
                });
              }
              if (canDuplicate) {
                menuItems.push({
                  key: 'duplicate',
                  icon: <CopyOutlined />,
                  label: 'Дублировать',
                  onClick: () => onDuplicate!(item.id),
                });
              }
              if (canChangeType) {
                menuItems.push({
                  key: 'change-type',
                  icon: <SwapOutlined />,
                  label: 'Сменить тип',
                  children: otherTypes.map((target) => ({
                    key: target.id,
                    label: `${target.icon ?? ''} ${target.name}`.trim(),
                    onClick: () => {
                      // Поля исходного типа, которых нет в целевом по slug, потеряются.
                      const targetSlugs = new Set((target.fields ?? []).map((f) => f.slug));
                      const lost = mainFields
                        .filter((f) => f.fieldType !== 'computed' && !targetSlugs.has(f.slug))
                        .map((f) => f.name);
                      Modal.confirm({
                        title: `Сменить тип на «${target.name}»?`,
                        okText: 'Сменить тип',
                        cancelText: 'Отмена',
                        content:
                          lost.length > 0 ? (
                            <div>
                              <p>Совпадающие поля перенесутся. Будут потеряны значения полей:</p>
                              <ul>{lost.map((n) => <li key={n}>{n}</li>)}</ul>
                              <p>Комментарии, история и связи сохранятся.</p>
                            </div>
                          ) : (
                            'Совпадающие поля перенесутся. Комментарии, история и связи сохранятся.'
                          ),
                        onOk: async () => {
                          try {
                            setChangingType(true);
                            const res = await changeItemType(spaceId, item.id, target.id);
                            const droppedMsg =
                              res.typeChange.dropped.length > 0
                                ? `, потеряно полей: ${res.typeChange.dropped.length}`
                                : '';
                            void message.success(`Тип изменён на «${target.name}»${droppedMsg}`);
                            onTypeChanged?.(item.id);
                            onClose();
                          } catch (err) {
                            void message.error(err instanceof Error ? err.message : 'Не удалось сменить тип');
                          } finally {
                            setChangingType(false);
                          }
                        },
                      });
                    },
                  })),
                });
              }
              if (canDelete) {
                if (menuItems.length > 0) {
                  menuItems.push({ key: 'sep', label: '', type: 'divider' });
                }
                menuItems.push({
                  key: 'delete',
                  icon: <DeleteOutlined />,
                  label: 'Удалить',
                  danger: true,
                  onClick: () => {
                    Modal.confirm({
                      title: 'Удалить запись?',
                      okText: 'Удалить',
                      okButtonProps: { danger: true },
                      cancelText: 'Отмена',
                      onOk: () => { onDelete!(item.id); onClose(); },
                    });
                  },
                });
              }

              // Нет ни одного действия по правам — меню не показываем.
              if (menuItems.length === 0) return null;

              return (
                <Dropdown trigger={['click']} menu={{ items: menuItems as never }} placement="bottomRight">
                  <Button
                    size="small"
                    type="text"
                    icon={<MoreOutlined />}
                    loading={applyingLinkRuleId !== null || changingType}
                    data-testid="drawer-overflow-menu"
                    aria-label="Действия"
                  />
                </Dropdown>
              );
            })()}
          </div>
        </div>
      }
      open={open}
      onCancel={handleDrawerClose}
      width={isFullscreen ? '100vw' : drawerSettings.settings.width}
      footer={null}
      centered={!isFullscreen}
      destroyOnHidden
      className={`${styles.cardModal}${isFullscreen ? ` ${styles.fullscreenModal}` : ''}`}
      styles={{
        body: isFullscreen
          // Высота — через flex от .ant-modal-content (100vh минус шапка),
          // а не через calc: не завязываемся на фактическую высоту шапки.
          ? { flex: '1 1 0', minHeight: 0, overflow: 'hidden', paddingRight: 12, display: 'flex', flexDirection: 'column' }
          // Оконный режим (и classic, и stream): высота адаптивная — по контенту
          // верха + минимум вкладок (autoBodyHeight). В stream верх подгоняется
          // под контент, поэтому у него нет своего скролла; модалка растёт вместе
          // с описанием, а короткая запись даёт короткую карточку без пустоты.
          // [card-flow] Оконный режим: высота по контенту с потолком — карточка
          // ровно такая, сколько в ней написано. Прокрутка одна, внутри потока.
          : {
              // Высота: на широком окне постоянная (две колонки, рамка не
              // прыгает — задаётся в CSS .cardModal по @media), на узком — по
              // содержанию с потолком: в одну колонку пустая запись иначе
              // превращалась в белый лист под композером.
              height: 'auto',
              maxHeight: 'calc(100vh - 120px)',
              overflow: 'hidden',
              paddingRight: 12,
              maxWidth: 1120,
              margin: '0 auto',
              display: 'flex',
              flexDirection: 'column',
            },
      }}
      data-testid="item-drawer"
    >
      {expandedField ? (
        // Тело модалки — flex-колонка с определённой высотой в обоих режимах,
        // поэтому полноэкранный редактор просто заполняет её флексом.
        <div style={{ display: 'flex', flexDirection: 'column', flex: '1 1 0', minHeight: 0 }}>
          <MarkdownEditorModal
            embedded
            open
            value={expandedField.value}
            title={expandedField.name}
            mode={expandedField.mode}
            fieldName={expandedField.name}
            itemId={item.id}
            onUploadImage={handleUploadImage}
            onClose={() => { expandedDraftRef.current = null; setExpandedField(null); }}
            onDraftChange={(next) => { expandedDraftRef.current = { slug: expandedField.slug, text: next }; }}
            onSave={(next) => {
              expandedDraftRef.current = null;
              const parsed = next.trim() === '' ? null : next;
              const current = item.fields[expandedField.slug] ?? null;
              if (onFieldSave && parsed !== current) onFieldSave(expandedField.slug, parsed);
            }}
          />
        </div>
      ) : (
      <>
      {(() => {
        // Единый рендер значения поля (inline-edit для редакторов, read-only
        // для viewer). Статусные поля показываются цветным чипом категории.
        const renderFieldCell = (field: FieldDefinition) => {
          const suggestKind = getSuggestKind(field);
          const rawValue = item.fields[field.slug] ?? null;
          const isWeight = field.slug === 'weight' && field.fieldType === 'number';
          const labelTooltip = isWeight ? 'Вес для приоритизации (0–100, отображается как % в связях)' : null;
          // Generic-единица измерения для любого number-поля из config.unit;
          // системный «weight» сохраняет '%' как дефолт.
          const unit = (field.config as { unit?: string } | null)?.unit ?? (isWeight ? '%' : null);
          const showUnit = unit != null && field.fieldType === 'number' && typeof rawValue === 'number';
          const statusChip = renderStatusChip(field, rawValue);
          const span = fieldGridSpan(field, rawValue, resolvedRefs);
          return (
            <div
              key={field.slug}
              className={`${styles.recSideField}${span === 'row' ? ` ${styles.propsRowSpan}` : ''}`}
            >
              <span className={styles.fieldLabel}>
                {labelTooltip ? (
                  <Tooltip title={labelTooltip}>
                    <span>{field.name}</span>
                  </Tooltip>
                ) : (
                  field.name
                )}
                {canEdit && suggestKind && (
                  <AiSuggestButton
                    itemId={item.id}
                    kind={suggestKind}
                    tooltip={getSuggestTooltip(suggestKind)}
                    fieldSlug={field.slug}
                    fieldName={field.name}
                  />
                )}
              </span>
              <span className={styles.fieldValueCell}>
                {onFieldSave && canEdit ? (
                  <span style={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0, width: '100%' }}>
                    <DrawerField
                      value={rawValue}
                      field={field}
                      resolvedRefs={resolvedRefs}
                      spaceId={spaceId}
                      statusChip={statusChip}
                      unit={showUnit ? unit : null}
                      onSave={(val) => onFieldSave(field.slug, val)}
                      onUploadImage={handleUploadImage}
                      onExpand={
                        field.fieldType === 'markdown' || field.fieldType === 'textarea'
                          ? (val) => setExpandedField({
                              slug: field.slug,
                              name: field.name,
                              value: val,
                              mode: field.fieldType === 'markdown' ? 'markdown' : 'textarea',
                            })
                          : undefined
                      }
                    />
                  </span>
                ) : (
                  <span style={{ display: 'flex', alignItems: 'center', minWidth: 0, maxWidth: '100%' }}>
                    {statusChip ?? renderFieldValue(rawValue, field, resolvedRefs)}
                    {showUnit && <span className={styles.fieldUnit}>{unit}</span>}
                  </span>
                )}
              </span>
            </div>
          );
        };

        // Тело записи (markdown/textarea) — главный объект слева, крупнее.
        // Секции сворачиваются кликом по лейблу (chevron), состояние
        // персистится в drawerSettings на пространство+тип — длинные
        // описания не съедают карточку, авто-высота верхней панели ужимается.
        const renderBodyField = (field: FieldDefinition) => {
          const rawValue = item.fields[field.slug] ?? null;
          const suggestKind = getSuggestKind(field);
          const collapsed = drawerSettings.settings.collapsedBodyFields.includes(field.slug);
          return (
            <div className={styles.recBodyField}>
              <span
                className={`${styles.fieldLabel} ${styles.bodyLabelToggle}`}
                onClick={() => drawerSettings.toggleBodyCollapsed(field.slug)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    drawerSettings.toggleBodyCollapsed(field.slug);
                  }
                }}
                title={collapsed ? 'Развернуть секцию' : 'Свернуть секцию'}
                data-testid={`body-section-toggle-${field.slug}`}
              >
                <DownOutlined
                  className={`${styles.bodyChevron}${collapsed ? ` ${styles.bodyChevronCollapsed}` : ''}`}
                />
                {field.name}
                {canEdit && suggestKind && (
                  <span onClick={(e) => e.stopPropagation()}>
                    <AiSuggestButton
                      itemId={item.id}
                      kind={suggestKind}
                      tooltip={getSuggestTooltip(suggestKind)}
                      fieldSlug={field.slug}
                      fieldName={field.name}
                    />
                  </span>
                )}
              </span>
              {!collapsed && (
              <div className={styles.recBodyValue}>
                {onFieldSave && canEdit ? (
                  <DrawerField
                    value={rawValue}
                    field={field}
                    resolvedRefs={resolvedRefs}
                    spaceId={spaceId}
                    onSave={(val) => onFieldSave(field.slug, val)}
                    onUploadImage={handleUploadImage}
                    onExpand={
                      field.fieldType === 'markdown' || field.fieldType === 'textarea'
                        ? (val) => setExpandedField({
                            slug: field.slug,
                            name: field.name,
                            value: val,
                            mode: field.fieldType === 'markdown' ? 'markdown' : 'textarea',
                          })
                        : undefined
                    }
                  />
                ) : (
                  renderFieldValue(rawValue, field, resolvedRefs)
                )}
              </div>
              )}
            </div>
          );
        };

        // ── [card-layout] Stream-скелет «Лента записи» ────────────────────
        // Один вертикальный поток: лид-поле (поле-заголовок крупным блоком,
        // правится на месте) → свойства во всю ширину (renderFieldCell в грид
        // auto-fill) → прочие body-поля (markdown/textarea) → вкладки,
        // забирающие остаток. Свойства стоят сразу под названием и не разрывают
        // текстовые блоки: раньше они лежали между описанием и комментариями, и
        // было неочевидно, куда писать текст. Нет двухпанельника, нет сплиттера, нет
        // зарезервированной пустоты: верх сжимается по контенту (CSS max-height),
        // низ (вкладки) растёт. Рендер полей переиспользуется как есть.
        if (streamMode) {
          const leadField = titleField;
          const extraBodyFields = bodyFields.filter((f) => f.slug !== leadField?.slug);
          // [props-table] Свойства — таблицей: каждое поле строкой «подпись |
          // значение», порядок — как задал админ, пустые тоже в таблице
          // прочерком (таблица терпит пустоту, россыпь плашек — нет). Тексты
          // — документом. Есть текстовые поля → текст слева, таблица справа
          // справкой; нет → таблица во всю ширину, длинная делится пополам.
          // Ничего не настраивается: раскладка следует из типов полей.
          const hasBody = extraBodyFields.length > 0;
          const filledBody = extraBodyFields.filter(
            (f) => !isEmptyFieldValue(item.fields[f.slug] ?? null) || openedEmptyBody.includes(f.slug),
          );
          const emptyBody = extraBodyFields.filter((f) => !filledBody.includes(f));
          const renderFieldRow = (field: FieldDefinition) => {
            const suggestKind = getSuggestKind(field);
            const rawValue = item.fields[field.slug] ?? null;
            // Единица измерения из config.unit; системный «weight» — проценты
            // по умолчанию (то же правило, что в renderFieldCell).
            const isWeight = field.slug === 'weight' && field.fieldType === 'number';
            const unit = (field.config as { unit?: string } | null)?.unit ?? (isWeight ? '%' : null);
            const showUnit = unit != null && field.fieldType === 'number' && typeof rawValue === 'number';
            const statusChip = renderStatusChip(field, rawValue);
            return (
              <div key={field.slug} className={styles.propsRow} data-testid={`props-row-${field.slug}`}>
                <div className={styles.propsLabel}>
                  <span>{field.name}</span>
                  {canEdit && suggestKind && (
                    <AiSuggestButton
                      itemId={item.id}
                      kind={suggestKind}
                      tooltip={getSuggestTooltip(suggestKind)}
                      fieldSlug={field.slug}
                      fieldName={field.name}
                    />
                  )}
                </div>
                <div className={styles.propsValue}>
                  {onFieldSave && canEdit ? (
                    <DrawerField
                      value={rawValue}
                      field={field}
                      resolvedRefs={resolvedRefs}
                      spaceId={spaceId}
                      statusChip={statusChip}
                      unit={showUnit ? unit : null}
                      onSave={(val) => onFieldSave(field.slug, val)}
                      onUploadImage={handleUploadImage}
                    />
                  ) : isEmptyFieldValue(rawValue) ? (
                    <span className={styles.propsEmpty}>—</span>
                  ) : (
                    <span style={{ display: 'flex', alignItems: 'center', minWidth: 0, maxWidth: '100%' }}>
                      {statusChip ?? renderFieldValue(rawValue, field, resolvedRefs)}
                      {showUnit && <span className={styles.fieldUnit}>{unit}</span>}
                    </span>
                  )}
                </div>
              </div>
            );
          };
          const renderPropsTable = (rows: FieldDefinition[], key: string) => (
            <div key={key} className={styles.propsTable}>{rows.map((f) => renderFieldRow(f))}</div>
          );
          // Без текста таблица идёт во всю ширину; длиннее восьми строк —
          // делится на две рядом, чтобы не превращаться в столб.
          // [compact] Свойства — плотной полосой под названием, в несколько
          // колонок по ширине (CSS auto-fill); строка 24px, двенадцать полей —
          // четыре строки. Разговор — справа от текста, когда он есть: лента
          // не должна уезжать за край экрана, пока справа пусто. Пустая лента
          // — композер одной строкой под текстом, без колонки.
          // Колонка разговора — всегда: у всех записей одна геометрия, при
          // листании ничего не прыгает. Пустая лента — композер сверху
          // колонки (CommentSection.module.css).
          const hasTalk = true;
          const commentsTab = tabItems.find((t) => t.key === 'comments');
          // Первой слева открывается вкладка, в которой есть что показать:
          // пустые «Связи» первым экраном — это дыра до футера, а «История»
          // непуста всегда.
          const activeRight = tabItems.find((t) => t.key === rightTab) ?? commentsTab;
          const propsBlock = showSidePanel ? (
            <div className={styles.propsBlock} data-testid="drawer-props-table">
              <Popover
                content={fieldSettingsContent}
                title="Видимые поля в карточке"
                trigger="click"
                open={showFieldSettings}
                onOpenChange={setShowFieldSettings}
                placement="bottomRight"
              >
                <Tooltip title="Настройки полей карточки">
                  <Button
                    size="small"
                    type="text"
                    className={styles.propsSettingsBtn}
                    icon={<SettingOutlined />}
                    onClick={(e) => e.stopPropagation()}
                    data-testid="drawer-field-settings"
                  />
                </Tooltip>
              </Popover>
              {panelFields.length === 0 ? (
                <div className={styles.fieldLabel}>Нет видимых полей</div>
              ) : (
                renderPropsTable(panelFields, 'a')
              )}
            </div>
          ) : null;
          const renderLead = (field: FieldDefinition) => {
            const rawValue = item.fields[field.slug] ?? null;
            // Лид крупным (18px) — ТОЛЬКО для однострочного text-заголовка. Для
            // markdown/textarea-заголовка (многострочный, напр. «Идея») размер
            // не раздуваем: рендерим как обычный документ-блок (14px), иначе
            // абзац-заголовок выглядит непропорционально большим.
            const isShortTitle = field.fieldType === 'text';
            return (
              <div className={isShortTitle ? styles.leadBlock : styles.leadBlockDoc}>
                {onFieldSave && canEdit ? (
                  <DrawerField
                    value={rawValue}
                    field={field}
                    resolvedRefs={resolvedRefs}
                    spaceId={spaceId}
                    onSave={(val) => onFieldSave(field.slug, val)}
                    onUploadImage={handleUploadImage}
                    onExpand={
                      field.fieldType === 'markdown' || field.fieldType === 'textarea'
                        ? (val) => setExpandedField({
                            slug: field.slug,
                            name: field.name,
                            value: val,
                            mode: field.fieldType === 'markdown' ? 'markdown' : 'textarea',
                          })
                        : undefined
                    }
                  />
                ) : (
                  renderFieldValue(rawValue, field, resolvedRefs)
                )}
              </div>
            );
          };
          return (
            // [card-flow] Одна прокрутка, высота по содержанию. Название во
            // всю ширину; ниже — текст слева и таблица свойств справа (или
            // таблица во всю ширину, если текста у типа нет); под текстом —
            // обсуждение, связи, история.
            <div className={styles.recRoot} ref={setRecRootEl}>
              <div ref={setTopContentEl} className={styles.recFlow}>
                <div
                  ref={colsRef}
                  className={`${styles.recTwoCol}${hasTalk ? ` ${styles.recTwoColSplit}` : ''}${hasTalk && docCollapsed ? ` ${styles.recDocCollapsed}` : ''}`}
                  style={talkPct != null ? ({ '--talk-width': `${talkPct}%` } as CSSProperties) : undefined}
                >
                <div className={styles.colDoc}>
                  {/* Название и свойства — в левой колонке: разговор справа
                      идёт на всю высоту карточки, от шапки до низа. */}
                  <div className={styles.streamContent}>
                    {leadField && renderLead(leadField)}
                    {propsBlock}
                  </div>
                  {hasBody && (
                  <div className={styles.streamContent}>
                    {filledBody.map((field) => (
                      <div key={field.slug} className={styles.streamBody}>{renderBodyField(field)}</div>
                    ))}
                    {emptyBody.length > 0 && canEdit && (
                      <div className={styles.addFieldRow} data-testid="drawer-empty-body-fields">
                        {emptyBody.map((field) => (
                          <button
                            key={field.slug}
                            type="button"
                            className={styles.addFieldBtn}
                            onClick={() => setOpenedEmptyBody((prev) => [...prev, field.slug])}
                          >
                            {`+ ${field.name}`}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  )}
                  <div className={styles.recFoot}>
                    <div className={styles.reactionBar}>
                      <ItemReactionBar spaceId={spaceId} itemId={item.id} canReact={canEdit} />
                    </div>
                    <ItemMetaFooter item={item} />
                  </div>
                </div>
                {hasTalk && commentsTab && (
                  <aside className={styles.colTalk}>
                    {/* Граница между текстом и обсуждением: тянуть — менять
                        ширину, двойной щелчок — вернуть по умолчанию. */}
                    <Tooltip
                      title={docCollapsed ? 'Показать запись' : 'Свернуть запись — панель на всю ширину'}
                      placement="right"
                    >
                      <button
                        type="button"
                        className={styles.docToggle}
                        onClick={() => setDocCollapsed((v) => !v)}
                        aria-label={docCollapsed ? 'Показать запись' : 'Свернуть запись'}
                        aria-expanded={!docCollapsed}
                        data-testid="drawer-doc-toggle"
                      >
                        {docCollapsed ? <DoubleRightOutlined /> : <DoubleLeftOutlined />}
                      </button>
                    </Tooltip>
                    {!docCollapsed && (
                    <div
                      className={styles.colResizer}
                      role="separator"
                      aria-orientation="vertical"
                      aria-label="Изменить ширину обсуждения"
                      title="Потяните, чтобы изменить ширину. Двойной щелчок — по умолчанию"
                      onPointerDown={startTalkResize}
                      onDoubleClick={resetTalkWidth}
                      data-testid="drawer-talk-resizer"
                    />
                    )}
                    {/* Разделы записи живут здесь, а не в шапке: слева всегда
                        сама запись, справа — что вокруг неё происходит. */}
                    <div className={styles.talkTabs} data-testid="drawer-right-tabs">
                      {tabItems.map((t) => (
                        <button
                          key={t.key}
                          type="button"
                          className={`${styles.talkTab}${rightTab === t.key ? ` ${styles.talkTabActive}` : ''}`}
                          onClick={() => setRightTab(t.key)}
                          data-testid={`drawer-right-tab-${t.key}`}
                        >
                          {t.key === 'comments' ? renderTabLabel('Обсуждение', tabCounts.comments) : t.label}
                        </button>
                      ))}
                    </div>
                    <div className={styles.talkBody}>{activeRight?.children}</div>
                  </aside>
                )}
                </div>
              </div>
            </div>
          );
        }

        // Двухпанельная карточка: слева — описание + активность (то, что читают
        // и пишут), справа — компактная панель «Свойства» (любое число полей
        // ложится вертикальным списком, без разъезда и пустот).
        // Нет markdown/textarea-полей → левой колонки-«тела» нет, и панель
        // «Свойства» растягивается на всю ширину карточки (иначе она жалась бы
        // в 312px справа, оставляя пустоту слева). В полноширинном режиме
        // короткие поля авто-заполняют колонки по ширине, как в широком режиме.
        const hasBody = bodyFields.length > 0;
        return (
          <div className={styles.recRoot} ref={setRecRootEl}>
          <Splitter
            layout="vertical"
            className={styles.recSplitter}
            onResize={(sizes) => {
              const top = sizes[0];
              if (typeof top === 'number') setLiveTopSize(top);
            }}
            onResizeEnd={(sizes) => {
              const top = sizes[0] ?? 0;
              const bottom = sizes[1] ?? 0;
              const total = top + bottom;
              if (total > 0) drawerSettings.setTopSize((top / total) * 100);
            }}
          >
          <Splitter.Panel size={liveTopSize} min={96} max="80%" className={styles.recTopPane}>
          <div
            ref={setTopContentEl}
            className={`${styles.recLayout} ${drawerSettings.settings.width >= 1300 ? styles.recLayoutWide : ''}`}
          >
            {hasBody && (
            <div className={styles.recMain}>
              {/* markdown/textarea-поля остаются в теле как полноценные
                  редакторы — КРОМЕ поля-заголовка: его значение уже редактируется
                  inline в шапке, и дублировать его в теле нельзя (два редактора на
                  одно значение). titleField исключён из bodyFields через
                  titleDedupSlug — так же, как из «Свойств». */}
              {bodyFields.map((field) => (
                <div key={field.slug} className={styles.recDesc}>{renderBodyField(field)}</div>
              ))}
            </div>
            )}
            {showSidePanel && (
            <aside className={`${styles.recSide} ${!hasBody ? styles.recSideFull : ''}`}>
              <div className={styles.sideHeader}>
                <span className={styles.sideTitle}>Свойства</span>
                <Popover
                  content={fieldSettingsContent}
                  title="Видимые поля в карточке"
                  trigger="click"
                  open={showFieldSettings}
                  onOpenChange={setShowFieldSettings}
                  placement="bottomRight"
                >
                  <Tooltip title="Настройки полей карточки">
                    <Button
                      size="small"
                      type="text"
                      icon={<SettingOutlined />}
                      onClick={(e) => e.stopPropagation()}
                      data-testid="drawer-field-settings"
                    />
                  </Tooltip>
                </Popover>
              </div>
              {panelFields.length === 0 ? (
                <div className={styles.fieldLabel}>Нет видимых полей</div>
              ) : (
                // [smart layout] Один общий грид со span'ами по фактическому
                // значению (fieldGridSpan): короткие значения — ячейка, длинные —
                // вся строка; auto-flow:dense подбивает дырки. Порядок полей —
                // как задал админ (заполненные и пустые вперемешку не
                // растаскиваем); пустые показываем всегда наравне с остальными.
                <div className={styles.propsGrid}>
                  {panelFields.map((field) => renderFieldCell(field))}
                </div>
              )}
            </aside>
            )}
          </div>
          </Splitter.Panel>
          <Splitter.Panel className={styles.recBottomPane}>
          <DraggableTabs
            items={tabItems}
            tabOrder={drawerSettings.settings.tabOrder}
            onTabOrderChange={drawerSettings.setTabOrder}
          />
          </Splitter.Panel>
          </Splitter>
          <div className={styles.reactionBar}>
            <ItemReactionBar spaceId={spaceId} itemId={item.id} canReact={canEdit} />
          </div>
          <ItemMetaFooter item={item} />
          </div>
        );
      })()}
      </>
      )}
    </Modal>
  );
}
